import React, { useEffect, useMemo, useRef, useState } from "react";
import { SEOHead } from "../components/SEO/SEOHead";
import styled, { css, keyframes } from "styled-components";
import Slider from "react-slick";
import { motion, AnimatePresence } from "framer-motion";
import { Link, useHistory } from "react-router-dom";
import { buildVendorParam } from "../utils/vendorSlug";
import CrawlLink from "../components/CrawlLink";
import { shopPath } from "../utils/seoPaths";
import { publicRequest } from "../api/requestMethods";
import { resolveBannerDestination } from "../utils/bannerLinks";
import { useDispatch, useSelector } from "react-redux";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { ReactComponent as ArrowLeftIcon } from "../assets/img/ArrowLeftIcon.svg";
import { ReactComponent as ArrowRightIcon } from "../assets/img/ArrowRightIcon.svg";
import ArrowLeftMuiIcon from "@mui/icons-material/ArrowLeft";
import ArrowRightMuiIcon from "@mui/icons-material/ArrowRight";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import VerifiedMuiIcon from "@mui/icons-material/Verified";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import Footer from "../components/Footer";
import AppDownloadShowcase from "../components/AppDownloadShowcase";
import VendorTagline from "../components/VendorTagline";
import {
  fetchPromoListings,
  fetchPromoProductDetails,
} from "../redux/listingRedux.js";
import Layout from "../components/Global/Layout.js";
import {
  fetchCategories,
  filterCategory,
  selectActiveCategories,
} from "../redux/categoriesRedux";
import QuantityControls from "../components/Buttons/QuantityControlButtons.js";
import { addToCart } from "../redux/cartRedux.js";
import {
  fetchRestaurants,
  setSelectedRestaurant,
} from "../redux/restaurantSlice.js";
import { fetchPromotedVendors } from "../redux/promotedVendorsSlice.js";
import useCoverageBatch from "../redux/useCoverageBatch";
import { setSelectedVendor } from "../redux/wholesaleRetailVendorSlice.js";
import placeholderRetailerImage from "../assets/retailerImage4.png";
import placeholderRestaurantImage from "../assets/restaurantPlaceholderResized.png";
import { landingCategoryImages } from "../assets/landing-categories";
import ResponsiveCategoryImage from "../components/ResponsiveCategoryImage";
import folkImage from "../assets/folk.png";
import healthcarePlaceholder from "../assets/healthcare_placeholder.png";
import healthcareBanner from "../assets/healthcare_plain.png";
import { setCurrentRestaurant } from "../redux/restaurantCartSlice.js";
import {
  fetchHealthcareVendors,
  setSelectedVendor as setSelectedHealthcareVendor,
} from "../redux/healthcareSlice.js";
import { setCurrentHealthcareVendor } from "../redux/healthcareCartSlice.js";
import EventsTile from "../components/Ticketing/EventsTile";
import { getOptimizedUrl } from "../utils/cloudinaryOptimize";
import BrandFeaturedImageCarousel from "../components/BrandFeaturedImageCarousel.jsx";
import {
  fetchFeaturedBrandsToggle,
  setFeaturedBrandsActive,
} from "../redux/brandSlice.js";
import { fetchBanners } from "../redux/bannerSlice.js";
import { io } from "socket.io-client";
import LocationPrompt from "../components/LocationPrompt.jsx";
import DeliveryBadge from "../components/DeliveryBadge.jsx";
import { VendorOfferTag } from "../components/DealTag";
import { COUNTRY_NAME, formatCurrency } from "../config/market";

// Custom arrow components
const PrevArrow = ({ className, style, onClick }) => (
  <ArrowButton className={className} style={{ ...style }} onClick={onClick}>
    <ArrowLeftIcon />
  </ArrowButton>
);

const NextArrow = ({ className, style, onClick }) => (
  <ArrowButton
    className={className}
    style={{ ...style, display: "block" }}
    onClick={onClick}
  >
    <ArrowRightIcon />
  </ArrowButton>
);

/**
 * Scroll a horizontal card rail by a WHOLE PAGE of cards.
 * ────────────────────────────────────────────────────────────────────────────
 * All three rails used to advance a hardcoded 300px. That was wrong twice over:
 *
 *   1. It is not a card. A restaurant card is 320px wide + 10px margins + a
 *      20px flex gap — roughly a 360px pitch. Advancing 300 left a card sliced
 *      in half at the edge, and the misalignment compounded on every click.
 *   2. It is not a page. On a wide screen showing four cards, one click moved
 *      less than one of them, so getting through 25 restaurants meant ~25
 *      clicks.
 *
 * Now the pitch is MEASURED from the two first cards' real positions, which
 * captures width + margin + gap without having to keep three sets of CSS
 * numbers in sync with this file. One click then advances a full visible page,
 * matching the categories slider (`slidesToScroll: slidesToShow`).
 */
const scrollRail = (containerId, direction) => {
  const container = document.getElementById(containerId);
  if (!container) return; // Section not rendered (empty data) — nothing to do.

  const first = container.children[0];
  const second = container.children[1];

  let pitch = 0;
  if (first && second) {
    // Distance between two card origins = width + margins + gap, exactly.
    pitch =
      second.getBoundingClientRect().left - first.getBoundingClientRect().left;
  } else if (first) {
    const gap = parseFloat(getComputedStyle(container).columnGap) || 0;
    pitch = first.getBoundingClientRect().width + gap;
  }
  // Degenerate measurement (hidden container, single card, mid-animation):
  // fall back to something sane rather than scrolling by 0 or NaN.
  if (!Number.isFinite(pitch) || pitch <= 0) pitch = 300;

  const perPage = Math.max(1, Math.floor(container.clientWidth / pitch));
  container.scrollBy({
    left: direction * pitch * perPage,
    behavior: "smooth",
  });
};

const scrollRestaurants = (direction) =>
  scrollRail("restaurantGrid", direction);
const scrollVendors = (direction) => scrollRail("vendorGrid", direction);
const scrollHealthcare = (direction) => scrollRail("healthcareGrid", direction);

/* ══ Loading placeholders ═══════════════════════════════════════════════
   Every skeleton on this page exists for exactly one reason: to hold the
   space its real content will occupy, so nothing moves when the data
   lands. A skeleton whose box is the wrong size is worse than no skeleton
   at all, because it guarantees a jump instead of merely risking one.

   Two rules follow, and the primitives below exist to enforce them:

   1. The painted block IS the reserved box. That is why this file no
      longer uses MUI's <Skeleton> for layout: with variant="text" MUI
      paints a `transform: scale(1, 0.6)`, so what you see is 60% of what
      the element actually occupies. It is very easy to eyeball such a
      skeleton, judge it "about right", and still be 40% out.

   2. A card skeleton is built from the REAL card's own styled components
      (see TopRestaurantCardSkeleton and friends near those cards), so its
      width, padding, borders and fixed-height children cannot drift away
      from the thing it stands in for. Only the text rows are hand-sized,
      and each one carries the arithmetic it mirrors. */

const skeletonShimmer = keyframes`
  0%   { background-position: -200% 0; }
  100% { background-position:  200% 0; }
`;

const SkeletonBlock = styled.div`
  width: ${(p) => p.$w || "100%"};
  height: ${(p) => p.$h || "16px"};
  margin-bottom: ${(p) => p.$mb || "0"};
  border-radius: ${(p) => (p.$r === undefined ? "6px" : p.$r)};
  flex-shrink: 0;
  background: linear-gradient(90deg, #e8e8e8 25%, #f4f4f4 50%, #e8e8e8 75%);
  background-size: 200% 100%;
  animation: ${skeletonShimmer} 1.4s ease-in-out infinite;
`;

/* The hero's frame, shared by the desktop slide, the mobile slide and the
   loading skeleton. Change it here or not at all.

   Before this existed the three disagreed badly. The skeleton was a flat
   `height: 400px` while the desktop slide had NO reserved height at all —
   `SliderImage` was `width: 100%; max-height: 280px` with no aspect ratio
   and no width/height attributes, so the browser could not know how tall
   the slide would be until the image decoded. Loading the page therefore
   went 400px → ~0 → whatever the image turned out to be, twice per visit.

   And "whatever the image turned out to be" was not one number. The
   hero_carousel slot declares 1600x900, but the banners actually in it
   run from 1.78:1 (mobile) to 6.34:1 (desktop) — so slides could differ
   in height from each other, and the rail resized as it auto-advanced.

   The ratios below are the shapes the artwork is really drawn at, and
   `object-fit: cover` makes every slide fill the frame regardless of what
   gets uploaded next. At a 1274px viewport this reproduces today's
   desktop height (201px) exactly, so the fix costs no visual change.
   Keep in lock-step with Lusuku_Vendor_Artwork_Guide.docx. */
const HERO_DESKTOP_RATIO =
  "3159 / 498"; /* 6.34:1 desktop banner artwork; the Ad Center crops desktop artwork to exactly this */
const HERO_MOBILE_RATIO = "16 / 9"; /* 1600x900 mobile banner artwork */

const heroFrame = css`
  position: relative;
  width: 100%;
  aspect-ratio: ${HERO_DESKTOP_RATIO};
  max-height: 280px;
  overflow: hidden;

  @media (max-width: 768px) {
    aspect-ratio: ${HERO_MOBILE_RATIO};
    max-height: none;
  }
`;

const CategoriesSkeleton = ({ sliderSettings }) => {
  /* Three faults used to live in this one function and they partly
     cancelled each other out, which is why nobody caught them:

     1. It opened its own <CategoriesContentContainer>, but the caller
        (CategoriesSection) already renders one and drops this inside it.
        The duplicate brought a second background, box-shadow, radius and
        ::before bar, none of which exist in the loaded branch.
     2. It painted a 30px rect above the rail that has no counterpart in
        the loaded tree at all — a phantom block.
     3. It rendered <StyledSliderCategories> bare, so react-slick fell
        back to slidesToShow: 1 while the real rail shows 4. Four pills
        laid out one-per-view instead of four-across.

     Fault 2 added ~30px and faults in CategoryPillSkeleton removed 20-34,
     so the net error looked like a harmless few pixels. Fixing any one of
     them alone would have made the shift WORSE. They go together. */
  return (
    <StyledSliderCategories {...sliderSettings} autoplay={false} arrows={false}>
      {Array.from(new Array(sliderSettings?.slidesToShow || 4)).map(
        (_, index) => (
          <div key={index}>
            <CategoryPillSkeleton aria-hidden="true">
              <SkeletonBlock $w="100%" $h="100%" $r="0" />
            </CategoryPillSkeleton>
          </div>
        ),
      )}
    </StyledSliderCategories>
  );
};

// Doc reference
// https://react-slick.neostack.com/docs/example/append-dots
const bannerSettings = {
  dots: true,
  fade: true,
  slidesToShow: 1,
  slidesToScroll: 1,
  infinite: true,
  speed: 500,
  autoplay: true,
  autoplaySpeed: 3500,
  adaptiveHeight: true, // Add this if images have different heights
  nextArrow: <NextArrow />,
  prevArrow: <PrevArrow />,
  afterChange: function () {
    let siblings = document.querySelectorAll(".slick-slide");
    let current = document.querySelector(".slick-current");
    // Set z-index of all slides to 0
    siblings.forEach((slide) => {
      slide.style.zIndex = "0";
    });
    // Set z-index of the current slide to 1 to ensure it's clickable
    if (current) {
      current.style.zIndex = "1";
    }
  },
  responsive: [
    {
      breakpoint: 1024,
      settings: {
        slidesToShow: 3,
        slidesToScroll: 3,
      },
    },
    {
      breakpoint: 768,
      settings: {
        arrows: false,
        slidesToShow: 2, // Even fewer items for mobile screens
        slidesToScroll: 1,
      },
    },
  ],
};

// Mobile-specific banner settings - optimized for mobile carousel
const mobileBannerSettings = {
  dots: true,
  fade: true,
  slidesToShow: 1,
  slidesToScroll: 1,
  infinite: true,
  speed: 500,
  autoplay: true,
  autoplaySpeed: 4000,
  adaptiveHeight: false,
  arrows: false, // No arrows on mobile for cleaner look
  afterChange: function () {
    let siblings = document.querySelectorAll(".slick-slide");
    let current = document.querySelector(".slick-current");
    siblings.forEach((slide) => {
      slide.style.zIndex = "0";
    });
    if (current) {
      current.style.zIndex = "1";
    }
  },
};

// Categories Slider
// const categorySliderSettings = {
//   dots: false,
//   infinite: true,
//   speed: 800,
//   slidesToShow: 4,
//   slidesToScroll: 1,
//   autoplay: autoplay,
//   autoplaySpeed: 3000,
//   pauseOnHover: true,
//   cssEase: "cubic-bezier(0.87, 0.03, 0.41, 0.9)",
//   nextArrow: <CustomNextArrow />,
//   prevArrow: <CustomPrevArrow />,
//   responsive: [
//     {
//       breakpoint: 1200,
//       settings: {
//         slidesToShow: 3,
//         slidesToScroll: 1,
//       },
//     },
//     {
//       breakpoint: 768,
//       settings: {
//         slidesToShow: 2,
//         slidesToScroll: 1,
//         arrows: true, // Keep arrows visible on tablet
//       },
//     },
//     {
//       breakpoint: 480,
//       settings: {
//         slidesToShow: 1.5, // Show one full and half of next slide
//         slidesToScroll: 1,
//         arrows: true,
//         infinite: true,
//         centerMode: true,
//         centerPadding: "40px",
//       },
//     },
//   ],
// };

// const CustomPrevArrow = ({ className, onClick }) => (
//   <NavigationArrow className={className} onClick={onClick} position="left">
//     <ArrowLeftIcon />
//   </NavigationArrow>
// );

// const CustomNextArrow = ({ className, onClick }) => (
//   <NavigationArrow className={className} onClick={onClick} position="right">
//     <ArrowRightIcon />
//   </NavigationArrow>
// );

// The hardcoded `sliderImages` fallback array used to live here. It has
// been MIGRATED into the Ad Center as real Advertisement rows (see server
// scripts/seedDefaultWebHeroBanners.js) so the hero is 100% backend-driven:
// admins schedule/publish/unpublish slides with metrics, and no client
// redeploy is ever needed to change artwork. If no banners exist for this
// viewer's device class the hero simply doesn't render.

const CategoriesSection = ({
  categories,
  selectedCategory,
  handleCategoryClick,
  isFetching,
}) => {
  const sliderRef = useRef(null);
  const [isHovered, setIsHovered] = useState(false);
  const [autoplay, setAutoplay] = useState(true);

  useEffect(() => {
    // Reset autoplay when categories change
    if (categories.length > 0 && !isHovered) {
      setAutoplay(true);
    }
  }, [categories, isHovered]);

  const sliderSettings = {
    dots: false,
    infinite: true,
    speed: 800,
    slidesToShow: 4,
    // Advance a FULL PAGE per arrow click, not one card. With 25 categories,
    // slidesToScroll:1 meant 25 clicks to see them all. Kept equal to
    // slidesToShow at every breakpoint below so the rail never leaves a card
    // half-visible.
    slidesToScroll: 4,
    autoplay: autoplay,
    autoplaySpeed: 3000,
    pauseOnHover: true,
    cssEase: "cubic-bezier(0.87, 0.03, 0.41, 0.9)",
    arrows: true,
    nextArrow: (
      <NavigationArrow position="right">
        <ArrowRightIcon />
      </NavigationArrow>
    ),
    prevArrow: (
      <NavigationArrow position="left">
        <ArrowLeftIcon />
      </NavigationArrow>
    ),
    responsive: [
      {
        breakpoint: 1200,
        settings: {
          slidesToShow: 3,
          slidesToScroll: 3,
        },
      },
      {
        breakpoint: 768,
        settings: {
          slidesToShow: 2,
          slidesToScroll: 2,
          arrows: true,
        },
      },
      {
        breakpoint: 480,
        settings: {
          slidesToShow: 1.5,
          slidesToScroll: 1,
          arrows: true,
          infinite: true,
          centerMode: true,
          centerPadding: "40px",
        },
      },
    ],
  };

  return (
    <CategoriesWrapper>
      <CategoriesContentContainer
        onMouseEnter={() => {
          setIsHovered(true);
          setAutoplay(false);
        }}
        onMouseLeave={() => {
          setIsHovered(false);
          setAutoplay(true);
        }}
      >
        <CategoriesHeader>
          <HeaderContent>
            <HeaderTitle>
              {isFetching ? (
                <LoadingText>Loading Categories...</LoadingText>
              ) : (
                <AnimatedText>BROWSE CATEGORIES</AnimatedText>
              )}
            </HeaderTitle>
            {!isFetching && (
              <CategoryCount>{categories.length} Categories</CategoryCount>
            )}
          </HeaderContent>
        </CategoriesHeader>

        {isFetching || categories.length === 0 ? (
          <CategoriesSkeleton sliderSettings={sliderSettings} />
        ) : (
          <AnimatePresence>
            <StyledSliderCategories ref={sliderRef} {...sliderSettings}>
              {categories.map((category, index) => (
                <motion.div
                  key={category._id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  // Capped: with forty categories, index * 0.1 left the last
                  // pill invisible for four seconds.
                  transition={{
                    duration: 0.3,
                    delay: Math.min(index, 8) * 0.06,
                  }}
                >
                  <CategoryPill
                    id={`category-${category.category}`}
                    isSelected={selectedCategory === category.category}
                    onClick={() => handleCategoryClick(category.category)}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <ImageContainer>
                      <CategoryImage
                        src={
                          /* The server fills category_pic with the group's
                             default tile when none was uploaded, and sends
                             displayPic for the same; the Cloudinary
                             placeholder only covers an older server. */
                          category.category_pic ||
                          category.displayPic ||
                          "https://res.cloudinary.com/lusuku/image/upload/v1702635550/Lusuku_Public_Resources/No_Category_Pic_Placecholder-Prod_ltsbhf.jpg"
                        }
                        alt={category.category}
                      />
                      <ImageOverlay />
                    </ImageContainer>
                    <CategoryName>{category.category}</CategoryName>
                  </CategoryPill>
                </motion.div>
              ))}
            </StyledSliderCategories>
          </AnimatePresence>
        )}
      </CategoriesContentContainer>
    </CategoriesWrapper>
  );
};

/* Each card's page, shared by its tap handler and its links so the two
   cannot drift. The ids are the ones the handlers have always used. */
const restaurantPathOf = (restaurant) =>
  `/restaurant/shop/${buildVendorParam(restaurant.name, restaurant.owner)}`;
const vendorPathOf = (vendor) =>
  `/shop/${buildVendorParam(vendor.name, vendor.owner || vendor._id)}`;
const healthcarePathOf = (vendor) =>
  `/healthcare/shop/${buildVendorParam(vendor.name, vendor.healthcareId)}`;
/* The link Google follows names the pharmacy's canonical address (owner id)
   when the record carries it; a tap keeps the id the handler has always used. */
const healthcareLinkOf = (vendor) =>
  shopPath("healthcare", {
    name: vendor.name,
    owner: vendor.owner,
    _id: vendor.healthcareId,
  }) || healthcarePathOf(vendor);
const promoPathOf = (id) => `/promoProduct/${id}`;

const VendorCardItem = ({
  vendor,
  index,
  vendorSearchQuery,
  onSelect,
  coverage,
  to,
}) => {
  const [heroLoaded, setHeroLoaded] = useState(false);
  const [heroError, setHeroError] = useState(false);
  const [avatarError, setAvatarError] = useState(false);

  /* `cardImageUrl` is the field the model documents for exactly this slot
     ("1280x720 storefront card/cover image"). Nobody has populated it yet,
     so in practice every card falls through to `imageUrl` — but preferring
     it means the card improves on its own the day a vendor uploads one,
     and it is the only asset here that is genuinely 16:9.

     `vendorHero` (c_fill) was the old preset and it was cropping the logos
     to pieces; see the comment on `vendorCard` for the measurements. */
  const cardImage = vendor.cardImageUrl || vendor.imageUrl;
  const heroSrc = cardImage
    ? getOptimizedUrl(cardImage, "vendorCard")
    : placeholderRetailerImage;

  /* The avatar is shown ONLY when the hero came from a distinct card image.
     With a single asset the old card rendered it twice, once cropped into
     the hero and once shrunk into the badge below it, which is a large part
     of why these cards read as empty: one picture, shown twice, plus a
     name. When there is only one image it gets one prominent place. */
  const hasDistinctCardImage = Boolean(
    vendor.cardImageUrl &&
    vendor.imageUrl &&
    vendor.cardImageUrl !== vendor.imageUrl,
  );
  const avatarSrc = vendor.imageUrl
    ? getOptimizedUrl(vendor.imageUrl, "vendorAvatar")
    : placeholderRetailerImage;

  /* What the vendor actually sells, in their own words. `typeOfGoods` is a
     free-text field on the model and the real values need tidying before
     they are shown: "Beauty " has a trailing space, "Beddings and beyond."
     a trailing stop, "Arrow root/KAKUPA powder" a shouted word. Same
     treatment as tidyName/tidyCuisine on the restaurants listing. */
  const sells = String(vendor.typeOfGoods || "")
    .trim()
    .replace(/\s{2,}/g, " ")
    .replace(/[.,;:]+$/, "")
    .replace(/\b[A-Z]{3,}\b/g, (w) => w[0] + w.slice(1).toLowerCase());

  const productCount = Number(vendor.productCount) || 0;
  const isVerified = vendor.sellerVerification === "VERIFIED";

  return (
    <TopVendorCard
      key={vendor._id}
      onClick={() => onSelect(vendor)}
      as={motion.div}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: index * 0.1 }}
      whileHover={{ y: -6, boxShadow: "0 20px 48px rgba(0,0,0,0.18)" }}
      $isHighlighted={
        vendorSearchQuery &&
        vendor.name.toLowerCase().includes(vendorSearchQuery.toLowerCase())
      }
    >
      {/* Hero Banner. A real link so search engines can reach the shop. */}
      <CrawlLink
        to={to}
        onNavigate={() => onSelect(vendor)}
        style={{ display: "block" }}
      >
        <TopVendorHero>
          {!heroLoaded && !heroError && <TopVendorHeroShimmer />}
          <TopVendorHeroImg
            src={heroError ? placeholderRetailerImage : heroSrc}
            alt={vendor.name}
            $loaded={heroLoaded}
            onLoad={() => setHeroLoaded(true)}
            onError={() => {
              setHeroError(true);
              setHeroLoaded(true);
            }}
          />
          <TopVendorHeroOverlay />
          {vendor.vendorType && (
            <TopVendorCategoryPill>{vendor.vendorType}</TopVendorCategoryPill>
          )}
          {/* Top-right: the category pill owns the top-left. */}
          <VendorOfferTag
            offer={vendor.offerSummary}
            corner="right"
            radius={16}
            size="m"
            zIndex={2}
          />
          {/* "Featured" said only that a staff member ticked `promoted`, which
              every card in this rail has by definition. Verified is keyed to
              the owner's real `sellerVerification`, so it is both true and
              answers what a first-time buyer is actually asking. Orange kept.
              See TopRestaurantVerifiedTag for the same reasoning. */}
          {isVerified && (
            <TopVendorVerifiedTag $belowDeal={Boolean(vendor.offerSummary)}>
              <VerifiedMuiIcon />
              Verified
            </TopVendorVerifiedTag>
          )}
        </TopVendorHero>
      </CrawlLink>

      {/* Card Body */}
      <TopVendorBody>
        {hasDistinctCardImage ? (
          <TopVendorAvatarRow>
            <TopVendorAvatar
              src={avatarError ? placeholderRetailerImage : avatarSrc}
              alt={vendor.name}
              onError={() => setAvatarError(true)}
            />
            {isVerified && (
              <TopVendorVerifiedBadge title="Verified Partner">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="white">
                  <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
                </svg>
              </TopVendorVerifiedBadge>
            )}
          </TopVendorAvatarRow>
        ) : (
          <TopVendorBodySpacer />
        )}

        <TopVendorName>
          <CrawlLink to={to} onNavigate={() => onSelect(vendor)}>
            {vendor.name.trim()}
          </CrawlLink>
        </TopVendorName>
        <CardTagline vendor={vendor} />
        {/* Was `vendor.promoCaption`, a field that does not exist anywhere on
            the RetailerWholesaler model or its controller — so this line was
            ALWAYS empty, and reserving two lines for it (which the rail needs
            to stay level) left every card with a blank gap under its name.
            `typeOfGoods` is the real field and it is populated for every
            promoted vendor today. It is also the more useful thing to say:
            "Arrow root / Kakupa powder" gives somebody a reason to look,
            where a street address gives them none. */}
        <TopVendorTagline>{sells}</TopVendorTagline>

        <TopVendorMeta>
          {productCount > 0 && (
            <TopVendorCountPill>
              {productCount} {productCount === 1 ? "product" : "products"}
            </TopVendorCountPill>
          )}
          <DeliveryBadge vendor={vendor} coverage={coverage} size="small" />
        </TopVendorMeta>

        <TopVendorCTA
          onClick={(e) => {
            e.stopPropagation();
            onSelect(vendor);
          }}
        >
          Shop Now
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            style={{ marginLeft: 6 }}
          >
            <path d="M5 12h14M12 5l7 7-7 7" />
          </svg>
        </TopVendorCTA>
      </TopVendorBody>
    </TopVendorCard>
  );
};

const LandingPage = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  // const { currentRestaurant } = useSelector((state) => state.restaurantCart);
  const { promoListings, isFetching } = useSelector((state) => state.products);
  const { restaurantData, isFetching: isFetchingRestaurants } = useSelector(
    (state) => state.restaurants,
  );
  const { promotedVendors, isFetching: isFetchingPromotedVendors } =
    useSelector((state) => state.promotedVendors);
  const { healthcareData, isFetching: isFetchingHealthcare } = useSelector(
    (state) => state.healthcare,
  );
  const { featuredBrandsActive } = useSelector((state) => state.brands);
  const selectedCategory = useSelector(
    (state) => state.categories.selectedCategory,
  );
  const userLocation = useSelector((state) => state.userLocation);
  const {
    desktopBanners,
    mobileBanners,
    isFetching: isFetchingBanners,
  } = useSelector((state) => state.banners);
  const isMountedCategory = useRef(false);
  const [showLocationPrompt, setShowLocationPrompt] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  const [showLeftArrowRestaurants, setShowLeftArrowRestaurants] =
    useState(false);
  const [showRightArrowRestaurants, setShowRightArrowRestaurants] =
    useState(true);

  const [showLeftArrowVendors, setShowLeftArrowVendors] = useState(false);
  const [showRightArrowVendors, setShowRightArrowVendors] = useState(true);

  const [showLeftArrowHealthcare, setShowLeftArrowHealthcare] = useState(false);
  const [showRightArrowHealthcare, setShowRightArrowHealthcare] =
    useState(true);

  // Restaurant search state
  const [restaurantSearchOpen, setRestaurantSearchOpen] = useState(false);
  const [restaurantSearchQuery, setRestaurantSearchQuery] = useState("");
  const restaurantSearchInputRef = useRef(null);

  // Vendor search state
  const [vendorSearchOpen, setVendorSearchOpen] = useState(false);
  const [vendorSearchQuery, setVendorSearchQuery] = useState("");
  const vendorSearchInputRef = useRef(null);

  // Healthcare search state
  const [healthcareSearchQuery, setHealthcareSearchQuery] = useState("");
  const healthcareSearchInputRef = useRef(null);

  const cartProducts = useSelector((state) => state.cart.products);

  // Define the isInCart function
  const isInCart = (productId) => {
    return cartProducts.some((product) => product._id === productId);
  };

  //getQuantity function
  const getQuantity = (productId) => {
    const product = cartProducts.find((p) => p._id === productId);
    return product ? product.qty : 0;
  };

  // Fetch categories from Redux store
  const categories = useSelector(selectActiveCategories);

  // Get Restaurants, Vendors, Healthcare, and Banners
  useEffect(() => {
    dispatch(fetchRestaurants());
    dispatch(fetchPromotedVendors());
    dispatch(fetchHealthcareVendors());
    dispatch(fetchBanners());
  }, [dispatch]);

  // Handle window resize for responsive banner rendering
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Check for user location on mount - only show popup on first visit
  useEffect(() => {
    if (
      !userLocation.latitude &&
      !userLocation.longitude &&
      !userLocation.isLoading &&
      !userLocation.hasSeenPrompt
    ) {
      const timer = setTimeout(() => {
        setShowLocationPrompt(true);
      }, 2000); // Show after 2 seconds
      return () => clearTimeout(timer);
    }
  }, [
    userLocation.latitude,
    userLocation.longitude,
    userLocation.isLoading,
    userLocation.hasSeenPrompt,
  ]);

  useEffect(() => {
    if (!promoListings.length) {
      dispatch(fetchPromoListings());
    }
  }, [dispatch, promoListings.length]);

  useEffect(() => {
    dispatch(fetchCategories());
  }, [dispatch]);

  useEffect(() => {
    dispatch(fetchFeaturedBrandsToggle());
  }, [dispatch]);

  useEffect(() => {
    const socketUrl = process.env.REACT_APP_SOCKET_URL;
    if (!socketUrl) {
      return undefined;
    }

    const socket = io(socketUrl, {
      transports: ["websocket", "polling"],
      withCredentials: true,
    });

    const handleFeaturedToggleUpdate = (payload = {}) => {
      const { featuredBrandsActive: active } =
        typeof payload === "object" && payload !== null ? payload : {};
      if (typeof active === "boolean") {
        dispatch(setFeaturedBrandsActive(active));
      }
    };

    socket.on("featuredBrandsToggleUpdated", handleFeaturedToggleUpdate);

    // Realtime Ad Center banners — the server broadcasts `advert:changed` on
    // every admin banner mutation (create / edit / publish / unpublish /
    // delete / slot move). Refetch the hero strip when the change touches the
    // hero_carousel slot so publish/unpublish reflects here immediately.
    // Small jitter so a fleet of open tabs doesn't refetch in the same second.
    let advertRefetchTimer = null;
    const handleAdvertChanged = (payload = {}) => {
      const changedSlots = [payload.slot, payload.prevSlot].filter(Boolean);
      if (!changedSlots.includes("hero_carousel")) return;
      if (advertRefetchTimer) clearTimeout(advertRefetchTimer);
      advertRefetchTimer = setTimeout(
        () => dispatch(fetchBanners()),
        Math.floor(Math.random() * 2000),
      );
    };

    socket.on("advert:changed", handleAdvertChanged);

    return () => {
      socket.off("featuredBrandsToggleUpdated", handleFeaturedToggleUpdate);
      socket.off("advert:changed", handleAdvertChanged);
      if (advertRefetchTimer) clearTimeout(advertRefetchTimer);
      socket.disconnect();
    };
  }, [dispatch]);

  const openPostDetail = (Id) => {
    return () => {
      dispatch(fetchPromoProductDetails(Id));
      history.push(promoPathOf(Id));
    };
  };

  // const handleRestaurantSelection = (restaurant) => {
  //   history.push({
  //     pathname: `/restaurant/${restaurant._id}`,
  //     state: { restaurant },
  //   });
  // };
  const handleRestaurantSelection = (restaurant) => {
    dispatch(setSelectedRestaurant(restaurant));
    dispatch(setCurrentRestaurant(restaurant.owner));
    history.push(restaurantPathOf(restaurant));
  };

  const handleVendorSelection = (vendor) => {
    dispatch(setSelectedVendor(vendor));
    history.push(vendorPathOf(vendor));
  };

  const handleHealthcareSelection = (vendor) => {
    dispatch(setSelectedHealthcareVendor(vendor));
    dispatch(setCurrentHealthcareVendor(vendor.healthcareId));
    history.push(healthcarePathOf(vendor));
  };

  /**
   * Open the shop ALREADY FILTERED to the category that was clicked.
   *
   * The `filterCategory` dispatch below was always here, but /shop seeded its
   * own local `filters.category` from DEFAULTS and never read that slice — so
   * the choice was dropped and every category card landed on "All products".
   * ShopCard now reads it; carrying the name in the URL too means a refresh,
   * a bookmark or a shared link keeps the filter, and the browser Back button
   * behaves.
   *
   * "All" is the catch-all category and the shop treats "no filter" as an
   * empty string, so it is deliberately NOT put in the URL — otherwise the API
   * would be filtered by a category literally named "All".
   */
  const openShopForCategory = (categoryName) => {
    dispatch(filterCategory(categoryName));
    const search = new URLSearchParams({ page: "1" });
    const name = String(categoryName || "").trim();
    if (name && name.toLowerCase() !== "all") search.set("category", name);
    history.push(`/shop?${search.toString()}`);
  };

  // Navigate to Selected Category on Click
  const handleCategoryClick = (categoryName) =>
    openShopForCategory(categoryName);

  // Navigate to Selected Category from the Banner
  const handleBannerClick = (categoryNameFromBanner) =>
    openShopForCategory(categoryNameFromBanner);

  // Custom banner links — admins can enter EITHER an absolute URL or a site
  // path ("/shop?page=1", "/restaurants", a product page…). Paths navigate
  // in-app (SPA, keeps state); absolute URLs open in a new tab.
  const openBannerLink = (target) => {
    if (!target) return;
    if (target.startsWith("/")) {
      history.push(target);
      return;
    }
    window.open(target, "_blank", "noopener,noreferrer");
  };

  // The WHOLE banner is the tap target, as it already is in the apps. The
  // corner button, when the Ad Center gave it text, is a second way in, not
  // the only one. Product and shop links carry a server-stamped
  // `linkParams.path`, so this never has to know how a product page is
  // addressed. Clicks are counted the same way the apps count them.
  const openBanner = (slide) => {
    const destination = resolveBannerDestination(slide);
    if (!destination) return;
    if (slide?._id) {
      publicRequest.post(`/advert/${slide._id}/click`).catch(() => {});
    }
    if (destination.kind === "category") {
      handleBannerClick(destination.value);
      return;
    }
    openBannerLink(destination.value);
  };

  const bannerKeyHandler = (slide) => (event) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      openBanner(slide);
    }
  };

  const handleAddToCartClick = (product, event) => {
    event.preventDefault(); // Prevents any default anchor action
    dispatch(addToCart({ item: product }));
  };

  // Scroll the active category pill into view only on user-triggered changes, not on mount
  useEffect(() => {
    if (!isMountedCategory.current) {
      isMountedCategory.current = true;
      return;
    }
    if (selectedCategory) {
      setTimeout(() => {
        /* getElementById, NOT querySelector: category names contain spaces
           ("Body Care"), which make `#category-Body Care` an (at best)
           never-matching selector — the pill silently never scrolled. */
        const activePill = document.getElementById(
          `category-${selectedCategory}`,
        );
        if (activePill) {
          activePill.scrollIntoView({
            behavior: "smooth",
            block: "nearest",
            inline: "center",
          });
        }
      });
    }
  }, [selectedCategory]);

  useEffect(() => {
    const restaurantGrid = document.getElementById("restaurantGrid");
    const vendorGrid = document.getElementById("vendorGrid");
    const healthcareGrid = document.getElementById("healthcareGrid");

    const checkScrollRestaurants = () => {
      setShowLeftArrowRestaurants(restaurantGrid.scrollLeft > 0);
      setShowRightArrowRestaurants(
        restaurantGrid.scrollLeft <
          restaurantGrid.scrollWidth - restaurantGrid.clientWidth,
      );
    };

    const checkScrollVendors = () => {
      setShowLeftArrowVendors(vendorGrid.scrollLeft > 0);
      setShowRightArrowVendors(
        vendorGrid.scrollLeft < vendorGrid.scrollWidth - vendorGrid.clientWidth,
      );
    };

    const checkScrollHealthcare = () => {
      if (healthcareGrid) {
        setShowLeftArrowHealthcare(healthcareGrid.scrollLeft > 0);
        setShowRightArrowHealthcare(
          healthcareGrid.scrollLeft <
            healthcareGrid.scrollWidth - healthcareGrid.clientWidth,
        );
      }
    };

    checkScrollRestaurants();
    checkScrollVendors();
    checkScrollHealthcare();

    restaurantGrid.addEventListener("scroll", checkScrollRestaurants);
    vendorGrid.addEventListener("scroll", checkScrollVendors);
    if (healthcareGrid) {
      healthcareGrid.addEventListener("scroll", checkScrollHealthcare);
    }

    return () => {
      restaurantGrid.removeEventListener("scroll", checkScrollRestaurants);
      vendorGrid.removeEventListener("scroll", checkScrollVendors);
      if (healthcareGrid) {
        healthcareGrid.removeEventListener("scroll", checkScrollHealthcare);
      }
    };
  }, []);

  const activeRestaurants = restaurantData.filter(
    (restaurant) =>
      restaurant.isActive &&
      restaurant.promoted &&
      restaurant.ownerDetails?.sellerVerification === "VERIFIED",
  );
  const activePromotedVendors = promotedVendors.filter(
    (vendor) => vendor.isActive,
  );
  const activeHealthcareVendors = healthcareData.filter(
    (vendor) => vendor.isActive,
  );

  // ── AUTHORITATIVE coverage for the landing-page vendor cards ─────────────
  // One batched backend check (POST /api/coverage/batch — the same
  // road-distance + radius verdict as the order-create gate) covering both
  // the promoted-vendor carousel and the healthcare strip, keyed by the id
  // the ORDER pipeline uses (owner || _id). The DeliveryBadge falls back to
  // its "~" client estimate only while this is loading or if it fails.
  const landingVendorId = (vendor) => vendor.owner || vendor._id;
  const landingCoverageIds = useMemo(
    () =>
      [
        ...activePromotedVendors.map(landingVendorId),
        ...activeHealthcareVendors.map(landingVendorId),
      ].filter(Boolean),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [promotedVendors, healthcareData],
  );
  const { byVendorId: landingCoverage } = useCoverageBatch({
    vendorIds: landingCoverageIds,
    enabled: landingCoverageIds.length > 0,
  });

  // Filter restaurants based on search query
  const filteredRestaurants = restaurantSearchQuery.trim()
    ? activeRestaurants.filter(
        (restaurant) =>
          restaurant.name
            .toLowerCase()
            .includes(restaurantSearchQuery.toLowerCase()) ||
          (restaurant.address &&
            restaurant.address
              .toLowerCase()
              .includes(restaurantSearchQuery.toLowerCase())),
      )
    : activeRestaurants;

  // Filter vendors based on search query
  const filteredVendors = vendorSearchQuery.trim()
    ? activePromotedVendors.filter(
        (vendor) =>
          vendor.name.toLowerCase().includes(vendorSearchQuery.toLowerCase()) ||
          (vendor.promoCaption &&
            vendor.promoCaption
              .toLowerCase()
              .includes(vendorSearchQuery.toLowerCase())),
      )
    : activePromotedVendors;

  // Filter healthcare vendors based on search query
  const filteredHealthcare = healthcareSearchQuery.trim()
    ? activeHealthcareVendors.filter(
        (vendor) =>
          vendor.name
            .toLowerCase()
            .includes(healthcareSearchQuery.toLowerCase()) ||
          (vendor.specializations &&
            vendor.specializations.some((s) =>
              s.toLowerCase().includes(healthcareSearchQuery.toLowerCase()),
            )),
      )
    : activeHealthcareVendors;

  // Handle restaurant search toggle
  const handleRestaurantSearchToggle = () => {
    setRestaurantSearchOpen(!restaurantSearchOpen);
    if (!restaurantSearchOpen) {
      setTimeout(() => {
        restaurantSearchInputRef.current?.focus();
      }, 300);
    } else {
      setRestaurantSearchQuery("");
    }
  };

  // Handle restaurant search input change
  const handleRestaurantSearchChange = (e) => {
    setRestaurantSearchQuery(e.target.value);
  };

  // Clear restaurant search
  const clearRestaurantSearch = () => {
    setRestaurantSearchQuery("");
    restaurantSearchInputRef.current?.focus();
  };

  // Handle vendor search toggle
  const handleVendorSearchToggle = () => {
    setVendorSearchOpen(!vendorSearchOpen);
    if (!vendorSearchOpen) {
      setTimeout(() => {
        vendorSearchInputRef.current?.focus();
      }, 300);
    } else {
      setVendorSearchQuery("");
    }
  };

  // Handle vendor search input change
  const handleVendorSearchChange = (e) => {
    setVendorSearchQuery(e.target.value);
  };

  // Clear vendor search
  const clearVendorSearch = () => {
    setVendorSearchQuery("");
    vendorSearchInputRef.current?.focus();
  };

  // Handle healthcare search input change
  const handleHealthcareSearchChange = (e) => {
    setHealthcareSearchQuery(e.target.value);
  };

  // Clear healthcare search
  const clearHealthcareSearch = () => {
    setHealthcareSearchQuery("");
    healthcareSearchInputRef.current?.focus();
  };

  return (
    <>
      <SEOHead
        title={`${COUNTRY_NAME}'s Online Marketplace`}
        description={`Order food, groceries, medicine & courier services. Fast delivery across ${COUNTRY_NAME}.`}
        canonicalPath="/"
        organization
      />

      {/* ── Floating AI Shopping Button (all pages) ─────────────────────── */}
      <AIFloatingButton
        href="https://chatgpt.com/g/g-69cae0c9f9c081918557f2d33395d5b0-lusuku-shopping-assistant"
        target="_blank"
        rel="noopener noreferrer"
        title="Shop with Ai — Lusuku Shopping Assistant"
      >
        <AIFloatingIcon>✦</AIFloatingIcon>
        <AIFloatingLabel>Shop with AI</AIFloatingLabel>
      </AIFloatingButton>

      <Layout>
        {/* Location Prompt Modal */}
        {showLocationPrompt && (
          <LocationPrompt onClose={() => setShowLocationPrompt(false)} />
        )}
        <PageContainer>
          <MainBanner>
            <CustomSliderDots>
              {isFetchingBanners ? (
                <BannerSkeleton aria-hidden="true">
                  <SkeletonBlock $w="100%" $h="100%" $r="0" />
                </BannerSkeleton>
              ) : isMobile ? (
                // Mobile Carousel - optimized for mobile screens
                <MobileBannerWrapper>
                  {mobileBanners.length > 0 ? (
                    <StyledSlider {...mobileBannerSettings}>
                      {mobileBanners.map((slide) => {
                        const clickable = !!resolveBannerDestination(slide);
                        return (
                          <div key={slide._id}>
                            <MobileSlideAspect
                              $clickable={clickable}
                              role={clickable ? "link" : undefined}
                              tabIndex={clickable ? 0 : undefined}
                              aria-label={
                                clickable
                                  ? slide.buttonText || slide.title
                                  : undefined
                              }
                              onClick={
                                clickable ? () => openBanner(slide) : undefined
                              }
                              onKeyDown={
                                clickable ? bannerKeyHandler(slide) : undefined
                              }
                            >
                              <MobileSliderImage
                                src={slide.imageUrlWebp || slide.imageUrl}
                                alt={slide.alt || slide.title}
                              />
                            </MobileSlideAspect>
                            {clickable && slide.buttonText && (
                              <BannerButton
                                onClick={(event) => {
                                  event.stopPropagation();
                                  openBanner(slide);
                                }}
                              >
                                {slide.buttonText}
                              </BannerButton>
                            )}
                          </div>
                        );
                      })}
                    </StyledSlider>
                  ) : // No admin banners for mobile: render nothing (backend-driven only)
                  null}
                </MobileBannerWrapper>
              ) : (
                // Desktop Carousel
                <StyledSlider {...bannerSettings}>
                  {desktopBanners.length > 0
                    ? desktopBanners.map((slide) => {
                        const clickable = !!resolveBannerDestination(slide);
                        return (
                          <div key={slide._id}>
                            <DesktopSlideAspect
                              $clickable={clickable}
                              role={clickable ? "link" : undefined}
                              tabIndex={clickable ? 0 : undefined}
                              aria-label={
                                clickable
                                  ? slide.buttonText || slide.title
                                  : undefined
                              }
                              onClick={
                                clickable ? () => openBanner(slide) : undefined
                              }
                              onKeyDown={
                                clickable ? bannerKeyHandler(slide) : undefined
                              }
                            >
                              {/* Desktop draws the strip-shaped artwork the
                                  Ad Center crops at 3159x498. Rows saved
                                  before desktop artwork existed fall back to
                                  their phone image, cover-cropped. */}
                              <SliderImage
                                src={
                                  slide.desktopImageUrlWebp ||
                                  slide.desktopImageUrl ||
                                  slide.imageUrl
                                }
                                alt={slide.alt || slide.title}
                              />
                            </DesktopSlideAspect>
                            {clickable && slide.buttonText && (
                              <BannerButton
                                onClick={(event) => {
                                  event.stopPropagation();
                                  openBanner(slide);
                                }}
                              >
                                {slide.buttonText}
                              </BannerButton>
                            )}
                          </div>
                        );
                      })
                    : // No admin banners for desktop: render nothing (backend-driven only)
                      null}
                </StyledSlider>
              )}
            </CustomSliderDots>
          </MainBanner>

          {/* Quick Categories Grid - Amazon-style */}
          <QuickCategoriesGrid>
            <QuickCategoryCard to="/restaurants">
              <QuickCategoryIcon>
                <QuickCategoryImage
                  sources={landingCategoryImages.restaurants}
                  alt="Food & Restaurants"
                />
              </QuickCategoryIcon>
              <QuickCategoryTitle>Food & Restaurants</QuickCategoryTitle>
              <QuickCategorySubtitle>Order meals →</QuickCategorySubtitle>
            </QuickCategoryCard>
            <QuickCategoryCard to="/shop?page=1">
              <QuickCategoryIcon>
                <QuickCategoryPhoto
                  sources={landingCategoryImages.groceries}
                  alt="Shop Groceries"
                />
              </QuickCategoryIcon>
              <QuickCategoryTitle>Shop Groceries</QuickCategoryTitle>
              <QuickCategorySubtitle>Browse products →</QuickCategorySubtitle>
            </QuickCategoryCard>
            <QuickCategoryCard to="/healthcare">
              <QuickCategoryIcon>
                <QuickCategoryPhoto
                  sources={landingCategoryImages.healthcare}
                  alt="Healthcare"
                />
              </QuickCategoryIcon>
              <QuickCategoryTitle>Healthcare</QuickCategoryTitle>
              <QuickCategorySubtitle>Pharmacies & more →</QuickCategorySubtitle>
            </QuickCategoryCard>
            <QuickCategoryCard to="/package-delivery">
              <QuickCategoryIcon>
                <QuickCategoryPhoto
                  sources={landingCategoryImages.packageDelivery}
                  alt="Send Package"
                />
              </QuickCategoryIcon>
              <QuickCategoryTitle>Send Package</QuickCategoryTitle>
              <QuickCategorySubtitle>Fast delivery →</QuickCategorySubtitle>
            </QuickCategoryCard>
            <EventsTile />
          </QuickCategoriesGrid>

          {
            /* Top Restaurants */
            <TopRestaurantsWrapper>
              <TopRestaurantsContentContainer>
                <TopRestaurantsHeader>
                  <TopRestaurantsHeaderContent>
                    <TopRestaurantsTitleWrapper>
                      <TopRestaurantsIcon>
                        <TopRestaurantsIconImg src={folkImage} alt="" />
                      </TopRestaurantsIcon>
                      <TopRestaurantsTitle>Top Restaurants</TopRestaurantsTitle>
                      {/* Collapsible Search */}
                      <RestaurantSearchContainer $isOpen={restaurantSearchOpen}>
                        <RestaurantSearchInput
                          ref={restaurantSearchInputRef}
                          type="text"
                          placeholder="Search restaurants..."
                          value={restaurantSearchQuery}
                          onChange={handleRestaurantSearchChange}
                          $isOpen={restaurantSearchOpen}
                        />
                        {restaurantSearchQuery && restaurantSearchOpen && (
                          <RestaurantSearchClearButton
                            onClick={clearRestaurantSearch}
                          >
                            <CloseIcon style={{ fontSize: 16 }} />
                          </RestaurantSearchClearButton>
                        )}
                        <RestaurantSearchButton
                          onClick={handleRestaurantSearchToggle}
                        >
                          {restaurantSearchOpen ? (
                            <CloseIcon style={{ fontSize: 20 }} />
                          ) : (
                            <SearchIcon style={{ fontSize: 20 }} />
                          )}
                        </RestaurantSearchButton>
                      </RestaurantSearchContainer>
                    </TopRestaurantsTitleWrapper>
                    <TopRestaurantsSubtitle>
                      Delicious meals delivered to your doorstep
                    </TopRestaurantsSubtitle>
                  </TopRestaurantsHeaderContent>
                </TopRestaurantsHeader>

                {/* No Results Message */}
                {!isFetchingRestaurants &&
                  restaurantSearchQuery &&
                  filteredRestaurants.length === 0 && (
                    <RestaurantNoResults>
                      <RestaurantNoResultsIcon>🔍</RestaurantNoResultsIcon>
                      <RestaurantNoResultsText>
                        No restaurants found for "{restaurantSearchQuery}"
                      </RestaurantNoResultsText>
                      <RestaurantNoResultsSubtext>
                        Try a different search term or browse all restaurants
                      </RestaurantNoResultsSubtext>
                    </RestaurantNoResults>
                  )}

                <TopRestaurantsProductGrid id="restaurantGrid">
                  {isFetchingRestaurants
                    ? Array.from(new Array(4)).map((_, index) => (
                        <TopRestaurantCardSkeleton key={index} />
                      ))
                    : filteredRestaurants.map((restaurant, index) => (
                        <TopRestaurantCard
                          key={restaurant._id}
                          onClick={() => handleRestaurantSelection(restaurant)}
                          as={motion.div}
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.4, delay: index * 0.1 }}
                          $isHighlighted={
                            restaurantSearchQuery &&
                            restaurant.name
                              .toLowerCase()
                              .includes(restaurantSearchQuery.toLowerCase())
                          }
                        >
                          <CrawlLink
                            to={restaurantPathOf(restaurant)}
                            onNavigate={() =>
                              handleRestaurantSelection(restaurant)
                            }
                            style={{ display: "block" }}
                          >
                            <TopRestaurantLogoWrapper>
                              <TopRestaurantLogo
                                src={
                                  restaurant.imageUrl ||
                                  placeholderRestaurantImage
                                }
                                alt={restaurant.name}
                              />
                              <VendorOfferTag
                                offer={restaurant.offerSummary}
                                corner="left"
                                radius={16}
                                size="m"
                                zIndex={2}
                              />
                              {/* Keyed to the verification the rail already
                                  requires, not to `promoted` — see the
                                  comment on TopRestaurantVerifiedTag. */}
                              {restaurant.ownerDetails?.sellerVerification ===
                                "VERIFIED" && (
                                <TopRestaurantVerifiedTag>
                                  <VerifiedMuiIcon />
                                  Verified
                                </TopRestaurantVerifiedTag>
                              )}
                            </TopRestaurantLogoWrapper>
                          </CrawlLink>
                          <TopRestaurantInfo>
                            <TopRestaurantName>
                              <CrawlLink
                                to={restaurantPathOf(restaurant)}
                                onNavigate={() =>
                                  handleRestaurantSelection(restaurant)
                                }
                              >
                                {restaurant.name}
                              </CrawlLink>
                            </TopRestaurantName>
                            <CardTagline vendor={restaurant} />
                            <TopRestaurantAddress title={restaurant.address}>
                              <LocationOnIcon />
                              <span>{restaurant.address}</span>
                            </TopRestaurantAddress>
                            <TopRestaurantOrderCta>
                              Order now
                              <ArrowRightMuiIcon style={{ fontSize: 18 }} />
                            </TopRestaurantOrderCta>
                          </TopRestaurantInfo>
                        </TopRestaurantCard>
                      ))}
                </TopRestaurantsProductGrid>
                <TopRestaurantsLeftArrow
                  style={{
                    display: showLeftArrowRestaurants ? "flex" : "none",
                  }}
                  onClick={() => scrollRestaurants(-1)}
                >
                  <ArrowLeftMuiIcon style={{ color: "white", fontSize: 25 }} />
                </TopRestaurantsLeftArrow>
                <TopRestaurantsRightArrow
                  style={{
                    display: showRightArrowRestaurants ? "flex" : "none",
                  }}
                  onClick={() => scrollRestaurants(1)}
                >
                  <ArrowRightMuiIcon style={{ color: "white", fontSize: 25 }} />
                </TopRestaurantsRightArrow>

                {/* More Restaurants Button */}
                <MoreRestaurantsButtonWrapper>
                  <MoreRestaurantsButton
                    onClick={() => history.push("/restaurants")}
                  >
                    View All Restaurants
                    <ArrowRightMuiIcon
                      style={{ marginLeft: "8px", fontSize: 20 }}
                    />
                  </MoreRestaurantsButton>
                </MoreRestaurantsButtonWrapper>
              </TopRestaurantsContentContainer>
            </TopRestaurantsWrapper>
          }

          {/* Featured Brands */}
          {featuredBrandsActive && <BrandFeaturedImageCarousel />}

          {/* Top Vendors */}
          <TopVendorsWrapper>
            <TopVendorsContentContainer>
              <TopVendorsSectionHeader>
                <TopVendorsSectionLeft>
                  <TopVendorsEyebrow>PARTNER SPOTLIGHT</TopVendorsEyebrow>
                  <TopVendorsTitle>Top Vendors</TopVendorsTitle>
                  <TopVendorsSubtitle>
                    Handpicked businesses delivering quality to your door
                  </TopVendorsSubtitle>
                </TopVendorsSectionLeft>
                <TopVendorsSectionRight>
                  <VendorSearchContainer $isOpen={vendorSearchOpen}>
                    <VendorSearchInput
                      ref={vendorSearchInputRef}
                      type="text"
                      placeholder="Search vendors..."
                      value={vendorSearchQuery}
                      onChange={handleVendorSearchChange}
                      $isOpen={vendorSearchOpen}
                    />
                    {vendorSearchQuery && vendorSearchOpen && (
                      <VendorSearchClearButton onClick={clearVendorSearch}>
                        <CloseIcon style={{ fontSize: 16 }} />
                      </VendorSearchClearButton>
                    )}
                    <VendorSearchButton onClick={handleVendorSearchToggle}>
                      {vendorSearchOpen ? (
                        <CloseIcon style={{ fontSize: 20 }} />
                      ) : (
                        <SearchIcon style={{ fontSize: 20 }} />
                      )}
                    </VendorSearchButton>
                  </VendorSearchContainer>
                  <TopVendorsViewAllLink
                    onClick={() => history.push("/vendors")}
                  >
                    View all →
                  </TopVendorsViewAllLink>
                </TopVendorsSectionRight>
              </TopVendorsSectionHeader>

              {/* No Results Message */}
              {!isFetchingPromotedVendors &&
                vendorSearchQuery &&
                filteredVendors.length === 0 && (
                  <VendorNoResults>
                    <VendorNoResultsIcon>🔍</VendorNoResultsIcon>
                    <VendorNoResultsText>
                      No vendors found for "{vendorSearchQuery}"
                    </VendorNoResultsText>
                    <VendorNoResultsSubtext>
                      Try a different search term or browse all vendors
                    </VendorNoResultsSubtext>
                  </VendorNoResults>
                )}

              <TopVendorsProductGrid id="vendorGrid">
                {isFetchingPromotedVendors
                  ? Array.from(new Array(4)).map((_, index) => (
                      <TopVendorCardSkeleton key={index} />
                    ))
                  : filteredVendors.map((vendor, index) => (
                      <VendorCardItem
                        key={vendor._id}
                        vendor={vendor}
                        index={index}
                        vendorSearchQuery={vendorSearchQuery}
                        onSelect={handleVendorSelection}
                        /* The link names the shop's canonical address (store
                           id); a tap still opens the owner-id address, which
                           the storefront rewrites to the same place. */
                        to={shopPath("retail", vendor) || vendorPathOf(vendor)}
                        coverage={
                          landingCoverage[String(landingVendorId(vendor))]
                        }
                      />
                    ))}
              </TopVendorsProductGrid>
              <TopVendorsLeftArrow
                style={{ display: showLeftArrowVendors ? "flex" : "none" }}
                onClick={() => scrollVendors(-1)}
              >
                <ArrowLeftMuiIcon style={{ color: "white", fontSize: 25 }} />
              </TopVendorsLeftArrow>
              <TopVendorsRightArrow
                style={{ display: showRightArrowVendors ? "flex" : "none" }}
                onClick={() => scrollVendors(1)}
              >
                <ArrowRightMuiIcon style={{ color: "white", fontSize: 25 }} />
              </TopVendorsRightArrow>

              {/* More Vendors Button */}
              <MoreVendorsButtonWrapper>
                <MoreVendorsButton onClick={() => history.push("/vendors")}>
                  Explore All Vendors
                  <ArrowRightMuiIcon
                    style={{ marginLeft: "8px", fontSize: 20 }}
                  />
                </MoreVendorsButton>
              </MoreVendorsButtonWrapper>
            </TopVendorsContentContainer>
          </TopVendorsWrapper>

          {/* Healthcare Vendors */}
          <HealthcareWrapper>
            <HealthcareContentContainer>
              <HealthcareTwoCol>
                {/* ── Left panel ── */}
                <HealthcareLeftPanel>
                  <HealthcareEyebrow>
                    <svg
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      style={{ marginRight: 8, flexShrink: 0 }}
                      aria-hidden="true"
                    >
                      <path
                        d="M9 2h6a1 1 0 0 1 1 1v5h5a1 1 0 0 1 1 1v6a1 1 0 0 1-1 1h-5v5a1 1 0 0 1-1 1H9a1 1 0 0 1-1-1v-5H3a1 1 0 0 1-1-1V9a1 1 0 0 1 1-1h5V3a1 1 0 0 1 1-1Z"
                        fill="#e91e63"
                      />
                    </svg>
                    Health &amp; Wellness
                  </HealthcareEyebrow>
                  <HealthcareTitle>
                    Your trusted
                    <br />
                    <span style={{ color: "#e91e63" }}>healthcare</span>{" "}
                    partners
                  </HealthcareTitle>
                  <HealthcareSubtitle>
                    Medicines, pharmacies and wellness products delivered to
                    your door.
                  </HealthcareSubtitle>

                  {/* Search bar */}
                  <HealthcareSearchRow>
                    <SearchIcon
                      style={{ color: "#c2185b", fontSize: 20, flexShrink: 0 }}
                    />
                    <HealthcareSearchInput
                      ref={healthcareSearchInputRef}
                      type="text"
                      placeholder="Search pharmacies or medicines…"
                      value={healthcareSearchQuery}
                      onChange={handleHealthcareSearchChange}
                      $isOpen
                    />
                    {healthcareSearchQuery && (
                      <HealthcareSearchClearButton
                        onClick={clearHealthcareSearch}
                      >
                        <CloseIcon style={{ fontSize: 16 }} />
                      </HealthcareSearchClearButton>
                    )}
                  </HealthcareSearchRow>

                  <MoreHealthcareButton
                    onClick={() => history.push("/healthcare")}
                  >
                    View All Healthcare
                    <ArrowRightMuiIcon
                      style={{ marginLeft: "8px", fontSize: 20 }}
                    />
                  </MoreHealthcareButton>
                </HealthcareLeftPanel>

                {/* ── Right panel — featured partner + providers row ── */}
                <HealthcareRightPanel>
                  {/* The loading state used to sit HERE, above the static
                      hero and outside HealthcareScrollWrap, while the real
                      providers row renders below the hero and inside it. So
                      on load a ~350px block did not resize, it MOVED across
                      the hero, taking the section's whole lower half with
                      it. The skeleton now lives in the real row's slot —
                      see "Healthcare Providers row" below. */}

                  {/* No results */}
                  {!isFetchingHealthcare &&
                    healthcareSearchQuery &&
                    filteredHealthcare.length === 0 && (
                      <HealthcareNoResults>
                        <HealthcareNoResultsIcon>🔍</HealthcareNoResultsIcon>
                        <HealthcareNoResultsText>
                          No results for &ldquo;{healthcareSearchQuery}&rdquo;
                        </HealthcareNoResultsText>
                        <HealthcareNoResultsSubtext>
                          Try a different term or browse all providers
                        </HealthcareNoResultsSubtext>
                      </HealthcareNoResults>
                    )}

                  {/* Static promotional hero (always rendered) */}
                  <HealthcareStaticHero
                    as={motion.div}
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.45 }}
                  >
                    <HealthcareStaticImage
                      src={healthcareBanner}
                      alt="Healthcare & Wellness"
                    />
                    <HealthcareStaticBadge>
                      <span style={{ marginRight: 6 }}>★</span> Featured
                    </HealthcareStaticBadge>
                  </HealthcareStaticHero>

                  {/* Healthcare Providers row (up to 3 vendors). Rendered
                      for the loading state too, so the header, the scroll
                      wrapper and the grid all exist from the first paint
                      and only their CONTENTS change. */}
                  {(isFetchingHealthcare || filteredHealthcare.length > 0) && (
                    <>
                      <HealthcareProvidersHeader>
                        <HealthcareProvidersTitle>
                          Healthcare Providers
                        </HealthcareProvidersTitle>
                        <HealthcareViewAllLink
                          onClick={() => history.push("/healthcare")}
                        >
                          View all
                          <ArrowRightMuiIcon
                            style={{ fontSize: 16, marginLeft: 4 }}
                          />
                        </HealthcareViewAllLink>
                      </HealthcareProvidersHeader>
                      <HealthcareScrollWrap>
                        <HealthcareProductGrid id="healthcareGrid">
                          {isFetchingHealthcare
                            ? Array.from(new Array(3)).map((_, index) => (
                                <HealthcareCardSkeleton key={index} />
                              ))
                            : filteredHealthcare
                                .slice(0, 3)
                                .map((vendor, index) => (
                                  <HealthcareCard
                                    key={vendor._id}
                                    onClick={() =>
                                      handleHealthcareSelection(vendor)
                                    }
                                    as={motion.div}
                                    initial={{ opacity: 0, y: 16 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{
                                      duration: 0.35,
                                      delay: index * 0.08,
                                    }}
                                    whileHover={{ y: -6, scale: 1.015 }}
                                    $isHighlighted={
                                      healthcareSearchQuery &&
                                      vendor.name
                                        .toLowerCase()
                                        .includes(
                                          healthcareSearchQuery.toLowerCase(),
                                        )
                                    }
                                  >
                                    <CrawlLink
                                      to={healthcareLinkOf(vendor)}
                                      onNavigate={() =>
                                        handleHealthcareSelection(vendor)
                                      }
                                      style={{ display: "block" }}
                                    >
                                      <HealthcareLogoWrapper
                                        $isLogoFallback={!vendor.bannerUrl}
                                      >
                                        <HealthcareLogo
                                          src={
                                            vendor.bannerUrl ||
                                            vendor.imageUrl ||
                                            healthcarePlaceholder
                                          }
                                          alt={vendor.name}
                                          $isLogoFallback={!vendor.bannerUrl}
                                        />
                                      </HealthcareLogoWrapper>
                                    </CrawlLink>
                                    <HealthcareCardBody>
                                      <HealthcareVendorName>
                                        <CrawlLink
                                          to={healthcareLinkOf(vendor)}
                                          onNavigate={() =>
                                            handleHealthcareSelection(vendor)
                                          }
                                        >
                                          {vendor.name}
                                        </CrawlLink>
                                      </HealthcareVendorName>
                                      <CardTagline vendor={vendor} />
                                      <HealthcareVerified>
                                        <span
                                          style={{
                                            color: "#2e7d32",
                                            marginRight: 4,
                                          }}
                                        >
                                          ✓
                                        </span>{" "}
                                        Verified Partner
                                      </HealthcareVerified>
                                      <DeliveryBadge
                                        vendor={vendor}
                                        coverage={
                                          landingCoverage[
                                            String(landingVendorId(vendor))
                                          ]
                                        }
                                        size="small"
                                      />
                                    </HealthcareCardBody>
                                  </HealthcareCard>
                                ))}
                        </HealthcareProductGrid>

                        <HealthcareLeftArrow
                          style={{
                            display: showLeftArrowHealthcare ? "flex" : "none",
                          }}
                          onClick={() => scrollHealthcare(-1)}
                        >
                          <ArrowLeftMuiIcon
                            style={{ color: "white", fontSize: 25 }}
                          />
                        </HealthcareLeftArrow>
                        <HealthcareRightArrow
                          style={{
                            display: showRightArrowHealthcare ? "flex" : "none",
                          }}
                          onClick={() => scrollHealthcare(1)}
                        >
                          <ArrowRightMuiIcon
                            style={{ color: "white", fontSize: 25 }}
                          />
                        </HealthcareRightArrow>
                      </HealthcareScrollWrap>
                    </>
                  )}
                </HealthcareRightPanel>
              </HealthcareTwoCol>
            </HealthcareContentContainer>
          </HealthcareWrapper>

          <ContentContainer>
            <ProductGridHeader>
              <DealsOffersTitle>DEALS & OFFERS</DealsOffersTitle>
              <DealsOffersViewMore to="/shop">
                See more products &gt;&gt;
              </DealsOffersViewMore>
              {/* <ViewMore to="/shop">See more products &gt;&gt;</ViewMore> */}
            </ProductGridHeader>
            <ProductGrid>
              {isFetching ? (
                Array.from(new Array(5)).map((_, index) => (
                  <ProductCardSkeleton key={index} />
                ))
              ) : promoListings.length === 0 ? (
                <DealsEmptyState>
                  <DealsEmptyEyebrow>No active offers</DealsEmptyEyebrow>
                  <DealsEmptyTitle>
                    Deals will appear here when they go live.
                  </DealsEmptyTitle>
                  <DealsEmptyText>
                    Browse the full marketplace while vendors prepare fresh
                    promotions.
                  </DealsEmptyText>
                  <DealsEmptyButton to="/shop">
                    Browse products
                  </DealsEmptyButton>
                </DealsEmptyState>
              ) : (
                promoListings.map((product) => (
                  <Card key={product._id}>
                    {/* productPad, not the raw upload. It is 600x600
                          c_pad on white, which is the square master this
                          card's frame now matches, and it stops a full-size
                          poster being shipped down a Ugandan mobile
                          connection to be painted at roughly 200px. The
                          preset's own doc comment names product_image as
                          its example, so this call site was the one it was
                          written for and never wired to. */}
                    {/* The picture is the tap target, so it carries the
                          link. The flex column copies the card's own, so the
                          full bleed photo sits exactly where it did. */}
                    <CrawlLink
                      to={promoPathOf(product._id)}
                      onNavigate={openPostDetail(product._id)}
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        width: "100%",
                      }}
                    >
                      <ProductImage
                        src={getOptimizedUrl(
                          product.product_image,
                          "productPad",
                        )}
                        alt={product.title}
                      />
                    </CrawlLink>
                    <ProductName>{product.title}</ProductName>
                    <ProductPrice>{formatCurrency(product.price)}</ProductPrice>
                    {isInCart(product._id) ? (
                      <QuantityControls
                        product={product}
                        quantity={getQuantity(product._id)}
                      />
                    ) : (
                      <AddToCartButton
                        onClick={(event) =>
                          handleAddToCartClick(product, event)
                        }
                      >
                        Add to Cart
                      </AddToCartButton>
                    )}
                  </Card>
                ))
              )}
            </ProductGrid>
          </ContentContainer>

          {/* Categories Slider */}
          <CategoriesSection
            categories={categories}
            selectedCategory={selectedCategory}
            handleCategoryClick={handleCategoryClick}
            isFetching={isFetching}
          />

          {/* App Download: three phones showing the app's own screens,
              drawn from the restaurants and deals this page already holds. */}
          <AppDownloadShowcase
            restaurants={activeRestaurants}
            deals={promoListings}
          />
        </PageContainer>
        <Footer />
      </Layout>
    </>
  );
};

export default LandingPage;

const ContentContainer = styled.div`
  max-width: 1200px;
  margin: 2rem auto;
  padding: 2rem 20px;
  background: linear-gradient(
    135deg,
    rgba(255, 255, 255, 0.95) 0%,
    rgba(248, 248, 248, 0.95) 100%
  );
  border-radius: 20px;
  box-shadow:
    0 8px 32px rgba(0, 0, 0, 0.1),
    0 0 0 1px rgba(0, 0, 0, 0.05);
  backdrop-filter: blur(10px);
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, transparent, #ef3b47, transparent);
  }

  @media (max-width: 768px) {
    margin: 1.5rem auto;
    padding: 1.5rem 15px;
    border-radius: 16px;
  }

  @media (max-width: 480px) {
    margin: 1rem 10px;
    padding: 1.2rem 12px;
    border-radius: 12px;
  }
`;

/* Mirrors CategoryPill exactly. Note what is NOT here: a width. The real
   pill declares none either — it fills its slick slide — so a fixed 240px
   here made the skeleton both the wrong width and the only element on the
   rail that refused to scale with the viewport. The heights and the
   `margin: 0 10px` are CategoryPill's, at CategoryPill's own breakpoints. */
const CategoryPillSkeleton = styled.div`
  height: 180px;
  border-radius: 16px;
  overflow: hidden;
  margin: 0 10px;
  background: #ececec;

  @media (max-width: 768px) {
    height: 150px;
  }

  @media (max-width: 480px) {
    height: 120px;
  }
`;

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 60vh;
  width: 100%;
  max-width: 100vw;
  align-items: stretch;
  margin: 0 auto;
  padding: 0;
  overflow-x: hidden;
`;

const ProductGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 1.5em;
  justify-items: center;
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  overflow-y: auto;
  max-height: 550px;
  padding: 0.5rem;

  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: rgba(239, 59, 71, 0.1);
    border-radius: 10px;
  }

  &::-webkit-scrollbar-thumb {
    background: linear-gradient(135deg, #ef3b47 0%, #d32f2f 100%);
    border-radius: 10px;

    &:hover {
      background: linear-gradient(135deg, #d32f2f 0%, #b71c1c 100%);
    }
  }

  /* These caps have to clear ONE WHOLE CARD, and the card got taller when
     the deal photo became square: its height now tracks the column width
     instead of being a flat ~265px. The worst case is the top of this
     range, 768px exactly, where two columns make a 366px card and a 366px
     photo, so the card lands near 490px. At the old 450px cap the first
     row was clipped inside a nested scroller, which reads as a broken
     layout rather than as something you can scroll. Each cap now clears
     that worst case with a deliberate strip of the next row showing, which
     is what tells a shopper the area scrolls at all.
     If the frame ratio in dealImageFrame ever changes, re-measure these. */
  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 1.2em;
    max-height: 560px;
  }

  @media (max-width: 480px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 1em;
    max-height: 430px;
    padding: 0.3rem;
  }
`;

const DealsEmptyState = styled.div`
  grid-column: 1 / -1;
  width: 100%;
  min-height: 220px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  padding: 2.5rem 1.5rem;
  text-align: center;
  border: 1px dashed rgba(239, 59, 71, 0.35);
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.72);

  @media (max-width: 480px) {
    min-height: 190px;
    padding: 2rem 1rem;
  }
`;

const DealsEmptyEyebrow = styled.span`
  color: #83b524;
  font-size: 0.78rem;
  font-weight: 800;
  letter-spacing: 1.2px;
  text-transform: uppercase;
`;

const DealsEmptyTitle = styled.h3`
  margin: 0;
  color: #334d55;
  font-size: 1.35rem;
  font-weight: 800;
  line-height: 1.25;

  @media (max-width: 480px) {
    font-size: 1.15rem;
  }
`;

const DealsEmptyText = styled.p`
  max-width: 440px;
  margin: 0;
  color: #667085;
  font-size: 0.95rem;
  line-height: 1.55;
`;

const DealsEmptyButton = styled(Link)`
  margin-top: 0.35rem;
  padding: 0.75rem 1.4rem;
  border-radius: 999px;
  background: #334d55;
  color: #ffffff;
  font-size: 0.88rem;
  font-weight: 700;
  text-decoration: none;
  transition:
    background 0.2s ease,
    transform 0.2s ease;

  &:hover {
    background: #263b42;
    transform: translateY(-1px);
  }
`;

const ProductName = styled.h3`
  font-size: 1rem;
  margin: 0.5rem 0;
`;

// Styles for the slick slider
const StyledSlider = styled(Slider)`
  width: 100%;

  .slick-list {
    overflow: hidden;
  }

  .slick-track {
    display: flex;
    align-items: center;
  }
  .slick-prev,
  .slick-next {
    z-index: 7;
    font-size: 24px;
    margin: 0 28px;
    // background-color: #BD2A2E;
    background-color: #152618;
  }
`;

const StyledSliderCategories = styled(Slider)`
  position: relative;
  width: 95%; // This will prevent full width while keeping responsiveness
  max-width: 1200px;
  margin: 0 auto;

  .slick-track {
    display: flex;
    gap: 1rem;
    padding: 1rem 0;
  }

  .slick-slide {
    padding: 0.5rem;
  }

  .slick-list {
    margin: 0 -0.5rem;
  }

  // Hide default arrows
  // .slick-prev,
  // .slick-next {
  //   display: none !important;
  // }

  @media (max-width: 480px) {
    width: 90%;
    margin: 0 auto;

    .slick-slide {
      padding: 0.3rem;
    }
  }
`;
// Custom CSS for the dots
const CustomSliderDots = styled.div`
  .slick-dots li button:before {
    color: #515151; // Change to the color for the dots
    font-size: 10px;
    opacity: 1;
  }

  .slick-dots li.slick-active button:before {
    color: #f4224b;
  }
`;

/* Desktop slide frame. Same rules as MobileSlideAspect, same source of
   truth (heroFrame) — the desktop branch simply never had one, which is
   why its height was whatever the current slide's image happened to be. */
const DesktopSlideAspect = styled.div`
  ${heroFrame}
  background: #111;
  cursor: ${(p) => (p.$clickable ? "pointer" : "default")};
  &:focus-visible {
    outline: 3px solid #7ac142;
    outline-offset: -3px;
  }
`;

const SliderImage = styled.img`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
  display: block;
`;

// Mobile-optimized slider image - fills the aspect-ratio container edge-to-edge.
// Percentage-based sizing means no dead space above/below on any screen width.
const MobileSliderImage = styled.img`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
  display: block;
`;

// Mobile slide frame. This was already doing the right thing on its own
// (a padding-top: 56.25% box locking the banner to 16:9); it now takes the
// same ratio from heroFrame instead, so the mobile slide, the desktop
// slide and the loading skeleton all read from one declaration.
const MobileSlideAspect = styled.div`
  ${heroFrame}
  background: #111;
  cursor: ${(p) => (p.$clickable ? "pointer" : "default")};
  &:focus-visible {
    outline: 3px solid #7ac142;
    outline-offset: -3px;
  }
`;

// Mobile banner wrapper for better mobile carousel styling
const MobileBannerWrapper = styled.div`
  width: 100%;

  .slick-dots {
    bottom: 8px;

    li button:before {
      font-size: 8px;
      color: #515151;
      opacity: 0.7;
    }

    li.slick-active button:before {
      color: #f4224b;
      opacity: 1;
    }
  }
`;

const MainBanner = styled.div`
  width: 100%;
  margin-top: -6px;
  box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.3);
  position: relative;
  overflow: hidden;
  background: #ffffff;
  border-radius: 0 0 20px 20px;

  .slick-slider {
    width: 100%;
  }

  .slick-dots {
    bottom: 14px;
  }

  @media (max-width: 768px) {
    border-radius: 0 0 12px 12px;
  }
`;

// Quick Categories Grid - Amazon-style above-the-fold cards
const QuickCategoriesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 16px;
  padding: 6px 24px;
  background: linear-gradient(180deg, #f0f0f0 0%, #ffffff 100%);

  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 12px;
    padding: 6px 16px;
  }

  @media (max-width: 480px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 10px;
    padding: 6px 12px;
  }
`;

const QuickCategoryCard = styled(Link)`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 6px 12px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  text-decoration: none;
  transition: all 0.3s ease;
  min-height: 0;
  border: 1px solid rgba(0, 0, 0, 0.05);

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.12);
    border-color: #83b524;
  }

  @media (max-width: 768px) {
    padding: 6px 10px;
    min-height: 0;
  }
`;

const QuickCategoryIcon = styled.div`
  font-size: 120px;
  line-height: 1;
  margin-bottom: 2px;

  @media (max-width: 768px) {
    font-size: 56px;
    margin-bottom: 4px;
  }
`;

const QuickCategoryImage = styled(ResponsiveCategoryImage)`
  width: 200px;
  height: auto;
  object-fit: contain;
  display: block;
  margin-bottom: -8px;

  @media (max-width: 768px) {
    width: auto;
    height: 56px;
  }
`;

const QuickCategoryPhoto = styled(ResponsiveCategoryImage)`
  height: 133px;
  width: auto;
  max-width: 100%;
  object-fit: contain;
  display: block;

  @media (max-width: 768px) {
    height: 56px;
  }
`;

const QuickCategoryTitle = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: #333;
  text-align: center;
  line-height: 1.3;

  @media (max-width: 768px) {
    font-size: 12px;
  }
`;

const QuickCategorySubtitle = styled.span`
  font-size: 11px;
  color: #83b524;
  margin-top: 4px;
  font-weight: 500;

  @media (max-width: 768px) {
    font-size: 10px;
  }
`;

const BannerButton = styled.button`
  position: absolute;
  bottom: 20px;
  right: 24px;
  padding: 10px 16px;
  border: none;
  background-color: #334d55;
  color: white;
  font-size: 14px;
  cursor: pointer;
  border-radius: 10px;
  box-shadow: 1px 1px 6px rgba(55, 250, 95, 98);
  &:hover {
    background-color: #598089;
    box-shadow: 0px 6px 10px rgba(0, 0, 0, 0.8);
  }

  @media (max-width: 480px) {
    font-size: 8px;
    padding: 3px 7px;
    bottom: 8px;
    right: 12px;
    border-radius: 6px;
  }
`;

// const SectionTitle = styled.h2`
//   font-size: 1.5rem;
//   color: #333;
//   margin-top: 10px;
//   margin-bottom: 8px;
//   padding-left: 10px;
// `;
const DealsOffersTitle = styled.h2`
  color: #e65100;
  font-size: 2.1rem;
  font-weight: 700;
  margin: 0;
  margin-bottom: 8px;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.1);
  letter-spacing: 1px;

  @media (max-width: 480px) {
    font-size: 1.6rem;
  }
`;

const Card = styled.div`
  /* The card's own padding, published as a custom property so the deal
     photo can cancel it and run full bleed. It is declared HERE and read
     by dealImageFrame rather than repeated there, because the padding has
     three breakpoint values and a hardcoded second copy would drift the
     first time somebody retunes one of them. */
  --deal-card-pad: 1.2em;

  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  border: 2px solid rgba(239, 59, 71, 0.1);
  padding: var(--deal-card-pad);
  text-align: center;
  background: linear-gradient(135deg, #ffffff 0%, #fafafa 100%);
  box-shadow:
    0 6px 20px rgba(0, 0, 0, 0.12),
    inset 0 1px 0 rgba(255, 255, 255, 0.8);
  transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  border-radius: 16px;
  margin-bottom: 18px;
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: linear-gradient(90deg, transparent, #ef3b47, transparent);
    opacity: 0;
    transition: opacity 0.3s;
  }

  &:hover {
    transform: translateY(-8px) scale(1.02);
    box-shadow:
      0 12px 30px rgba(239, 59, 71, 0.2),
      0 8px 16px rgba(0, 0, 0, 0.15);
    border-color: #ef3b47;
  }

  &:hover::before {
    opacity: 1;
  }

  @media (max-width: 768px) {
    --deal-card-pad: 1em;
    border-radius: 14px;
  }

  @media (max-width: 480px) {
    --deal-card-pad: 0.9em;
    border-radius: 12px;
    margin-bottom: 12px;
  }
`;

/* `height: auto` reserved nothing, so every deals card grew by the height
   of its photo the moment that photo decoded — and no skeleton could
   predict by how much. 3/2 is not a guess: the productCard Cloudinary
   preset is 600x400, so that is the shape actually delivered here.
   `contain` rather than `cover` because a product that arrives at some
   other ratio should letterbox, never be cropped or stretched. */
/* The geometry of a deal photo, in ONE place, because the real image and
   its loading skeleton have to agree or the grid jumps the moment the fetch
   lands. The skeleton used to carry its own copy with a `== ProductImage`
   comment holding the two together, which is a comment doing a compiler's
   job; both now read this.

   Two things were wrong with what this replaced, and they compounded.

   The frame was `aspect-ratio: 3 / 2`, a LANDSCAPE box, while a deal poster
   is square. `object-fit: contain` then fits a square into a landscape box
   by its HEIGHT, so the photo rendered at two thirds of the width it was
   given and the card sat two thirds empty. On top of that `max-width: 200px`
   capped it below the track width, and the card's own 1.2em padding took
   another 2.4em off. The poster is the thing a shopper is scanning for and
   it was the smallest element on its own card.

   So: a SQUARE frame, and full bleed to the card's edges. Square is not a
   guess about today's artwork, it is the frame that cannot lose. A square
   source now fills it completely, a landscape source uses the full width
   exactly as it did before, and a portrait source uses the full height. No
   aspect is worse off than it was under 3 / 2.

   `contain` STAYS, and is not negotiable: these posters carry a price
   badge burnt into the corner and a printed contents list. `cover` would
   crop the number the deal exists to advertise.

   The width overshoots the content box by twice the card padding and the
   card centres it, so it lands exactly on the padding edges left and right;
   the negative top margin does the same at the top. The card is already
   `overflow: hidden` with a radius, so the corners clip for free. */
const dealImageFrame = css`
  display: block;
  width: calc(100% + var(--deal-card-pad, 1.2em) * 2);
  margin-top: calc(var(--deal-card-pad, 1.2em) * -1);
  aspect-ratio: 1 / 1;
  height: auto;
`;

const ProductImage = styled.img`
  ${dealImageFrame}
  object-fit: contain;
  /* contain letterboxes anything that is not square. On white it reads as
     a product shot with breathing room; on the card's grey gradient it
     reads as a broken image. */
  background: #fff;
  cursor: pointer;
`;

const AddToCartButton = styled.button`
  color: white;
  background: linear-gradient(135deg, #334d55 0%, #2a3f47 100%);
  border: none;
  border-radius: 12px;
  padding: 0.8rem 1.5rem;
  margin-top: 12px;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  font-weight: 600;
  font-size: 0.95rem;
  box-shadow:
    0 4px 12px rgba(51, 77, 85, 0.3),
    inset 0 1px 0 rgba(255, 255, 255, 0.2);
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    top: 50%;
    left: 50%;
    width: 0;
    height: 0;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.2);
    transform: translate(-50%, -50%);
    transition:
      width 0.6s,
      height 0.6s;
  }

  &:hover::before {
    width: 300px;
    height: 300px;
  }

  &:hover {
    background: linear-gradient(135deg, #598089 0%, #4a6770 100%);
    transform: translateY(-2px);
    box-shadow:
      0 6px 16px rgba(51, 77, 85, 0.4),
      inset 0 1px 0 rgba(255, 255, 255, 0.3);
  }

  &:active {
    transform: translateY(0);
  }

  @media (max-width: 768px) {
    padding: 0.7rem 1.3rem;
    font-size: 0.9rem;
  }

  @media (max-width: 480px) {
    padding: 0.65rem 1.2rem;
    font-size: 0.85rem;
    width: 100%;
  }
`;

const ProductGridHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  padding-bottom: 1rem;
  border-bottom: 2px solid rgba(239, 59, 71, 0.1);

  @media (max-width: 768px) {
    margin-bottom: 1.5rem;
    padding-bottom: 0.8rem;
    flex-wrap: wrap;
    gap: 1rem;
  }

  @media (max-width: 480px) {
    margin-bottom: 1rem;
    padding-bottom: 0.6rem;
    gap: 0.8rem;
  }
`;

// const ViewMore = styled(Link)`
//   font-size: 14px;
//   font-style: italic;
//   text-decoration: none;
//   padding: 0.3rem 1rem; // Pill shape typically has larger padding
//   color: white; // Text color
//   background-color: #ef3b47; // Slightly lighter color on hover
//   border-radius: 10px; // Fully rounded corners for pill shape
//   cursor: pointer;
//   transition: background-color 0.3s ease-in-out, transform 0.3s ease-in-out; // Smooth transitions for color and transform

//   &:hover {
//     background-color: #445e69;
//     text-decoration: underline;
//     transform: scale(1.05); // Slightly increase the size on hover
//   }
//   ${mobile({
//     fontSize: "11px",
//     padding: "0.2rem 0.8rem",
//   })}
// `;
const DealsOffersViewMore = styled(Link)`
  font-size: 12px;
  font-style: normal;
  text-decoration: none;
  padding: 0.75rem 1.8rem;
  color: white;
  background: linear-gradient(135deg, #ef3b47 0%, #d32f2f 100%);
  border-radius: 30px;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  box-shadow:
    0 4px 15px rgba(239, 59, 71, 0.4),
    inset 0 1px 0 rgba(255, 255, 255, 0.3);
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 1px;
  position: relative;
  overflow: hidden;
  border: 2px solid rgba(255, 255, 255, 0.2);

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(255, 255, 255, 0.3),
      transparent
    );
    transition: left 0.5s;
  }

  &:hover::before {
    left: 100%;
  }

  &:hover {
    background: linear-gradient(135deg, #d32f2f 0%, #b71c1c 100%);
    transform: translateY(-3px) scale(1.05);
    box-shadow:
      0 8px 20px rgba(239, 59, 71, 0.5),
      inset 0 1px 0 rgba(255, 255, 255, 0.4);
  }

  &:active {
    transform: translateY(-1px) scale(1.02);
  }

  @media (max-width: 768px) {
    font-size: 13px;
    padding: 0.65rem 1.5rem;
    letter-spacing: 0.8px;
  }

  @media (max-width: 480px) {
    font-size: 11px;
    padding: 0.6rem 1.2rem;
    letter-spacing: 0.5px;
    width: 100%;
    text-align: center;
  }
`;

/* Replaces the old free-standing ProductSkeleton, which declared its own
   border, padding, radius and — the actual bug — `margin: 30px`. The real
   Card has no side margins and fills its 209.6px grid track, so a 200px
   skeleton plus 60px of margin was a 260px margin box inside a 209.6px
   column: it overflowed the track by 50px and bled ~25px each side.
   Building on Card removes the whole class of error. */
const ProductImageSkeleton = styled(SkeletonBlock)`
  /* Same frame as the real photo, read from the same place rather than
     copied, so the card cannot change height when the fetch lands. */
  ${dealImageFrame}
`;

const ProductCardSkeleton = () => (
  <Card as="div" aria-hidden="true">
    <ProductImageSkeleton />
    {/* ProductName 1rem with 0.5rem margins, ProductPrice 1em with 5px
        margins, AddToCartButton 0.8rem/1.5rem padding + 12px margin-top. */}
    <SkeletonBlock $w="82%" $h="19px" $mb="16px" />
    <SkeletonBlock $w="46%" $h="19px" $mb="10px" />
    <SkeletonBlock $w="80%" $h="44px" $r="10px" />
  </Card>
);

const ProductPrice = styled.span`
  display: block;
  font-size: 1em;
  color: #333;
  margin: 5px 0;
`;

/* Was a flat `height: 400px`, against a real hero that renders 201px on
   desktop and 171px on a 320px phone. Every single page load therefore
   collapsed the banner by ~200px and dragged the whole page up with it.
   It now uses the same frame the slides do, so the two cannot disagree. */
const BannerSkeleton = styled.div`
  ${heroFrame}
`;

const CategoryImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s ease;
`;

const CategoryName = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 1rem;
  color: white;
  font-weight: 600;
  text-align: center;
  background: linear-gradient(to top, rgba(0, 0, 0, 0.95), rgba(0, 0, 0, 0.7));
  font-size: 1.05rem;
  transition: all 0.3s ease;
  letter-spacing: 0.5px;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.5);
  border-top: 2px solid rgba(252, 186, 20, 0.3);

  @media (max-width: 768px) {
    padding: 0.7rem;
    font-size: 0.95rem;
  }

  @media (max-width: 480px) {
    padding: 0.6rem;
    font-size: 0.85rem;
  }
`;

const CategoryPill = styled(motion.div)`
  background: rgba(255, 255, 255, 0.12);
  border-radius: 16px;
  overflow: hidden;
  cursor: pointer;
  height: 180px;
  position: relative;
  transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  border: 3px solid
    ${(props) => (props.isSelected ? "#FCBA14" : "rgba(255, 255, 255, 0.1)")};
  margin: 0 10px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: ${(props) =>
      props.isSelected
        ? "linear-gradient(90deg, #FCBA14, #f5a623, #FCBA14)"
        : "transparent"};
    background-size: 200% 100%;
    animation: ${(props) =>
      props.isSelected ? "shimmerCategory 2s linear infinite" : "none"};
    z-index: 2;
  }

  @keyframes shimmerCategory {
    0% {
      background-position: -200% 0;
    }
    100% {
      background-position: 200% 0;
    }
  }

  &:hover {
    transform: translateY(-8px) scale(1.02);
    box-shadow:
      0 12px 24px rgba(252, 186, 20, 0.3),
      0 8px 16px rgba(0, 0, 0, 0.4);
    border-color: #fcba14;

    ${CategoryImage} {
      transform: scale(1.15);
    }
  }

  @media (max-width: 768px) {
    height: 150px;
  }

  @media (max-width: 480px) {
    height: 120px;
  }
`;

const ImageOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(
    to bottom,
    rgba(0, 0, 0, 0) 0%,
    rgba(0, 0, 0, 0) 50%,
    rgba(0, 0, 0, 0.6) 100%
  );
  transition: opacity 0.3s ease;

  ${CategoryPill}:hover & {
    background: linear-gradient(
      to bottom,
      rgba(252, 186, 20, 0.05) 0%,
      rgba(0, 0, 0, 0.1) 50%,
      rgba(0, 0, 0, 0.7) 100%
    );
  }
`;

const CategoriesHeader = styled.div`
  background: linear-gradient(135deg, #fcba14 0%, #f5a623 100%);
  padding: 20px;
  border-radius: 12px;
  box-shadow:
    0 6px 16px rgba(252, 186, 20, 0.4),
    0 2px 8px rgba(0, 0, 0, 0.3);
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(255, 255, 255, 0.3),
      transparent
    );
    animation: shine 3s infinite;
  }

  @keyframes shine {
    0% {
      left: -100%;
    }
    100% {
      left: 100%;
    }
  }
`;

// const CategoriesHeaderContent = styled.div`
//   margin: 0 auto;
// `;

const ArrowButton = styled.button`
  z-index: 7;
  width: 30px;
  height: 30px;
  background: black;
  border-radius: 15px; // Half of width/height to create circle
  color: white;
  border: none;
  cursor: pointer;
  align-items: center;
  justify-content: center;
  display: flex;

  // hide default arrows
  &:before {
    content: none;
  }

  &:hover {
    background: #333; // Darken button on hover
  }
`;

const CategoriesWrapper = styled.div`
  margin: 2rem 0;
  background: linear-gradient(135deg, #344d55 0%, #1a2f35 100%);
  padding: 2rem 0;
  overflow: hidden;
  position: relative;

  &::before {
    content: "";
    position: absolute;
    top: -50%;
    left: -50%;
    width: 200%;
    height: 200%;
    background: radial-gradient(
      circle,
      rgba(252, 186, 20, 0.08) 0%,
      transparent 70%
    );
    animation: pulseGlow 8s ease-in-out infinite;
  }

  @keyframes pulseGlow {
    0%,
    100% {
      transform: scale(1) rotate(0deg);
      opacity: 0.5;
    }
    50% {
      transform: scale(1.1) rotate(180deg);
      opacity: 0.8;
    }
  }

  @media (max-width: 480px) {
    margin: 1rem 0;
    padding: 1rem 0;
  }
`;

const CategoriesContentContainer = styled.div`
  width: 95%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1rem;
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.08);
  backdrop-filter: blur(12px);
  box-shadow:
    0 8px 24px rgba(0, 0, 0, 0.4),
    0 0 0 1px rgba(252, 186, 20, 0.1);
  overflow: visible;
  position: relative;
  z-index: 1;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: linear-gradient(90deg, transparent, #fcba14, transparent);
    border-radius: 16px 16px 0 0;
  }

  @media (max-width: 480px) {
    width: 90%;
    padding: 0 0.5rem;
    border-radius: 12px;
  }
`;

const HeaderContent = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 2rem;

  @media (max-width: 480px) {
    padding: 0.8rem 1rem;
    flex-direction: column;
    gap: 0.5rem;
  }
`;

const HeaderTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const AnimatedText = styled(motion.h2)`
  color: #8b0000;
  font-size: 1.8rem;
  font-weight: 700;
  margin: 0;
  letter-spacing: 2px;
  text-shadow:
    2px 2px 4px rgba(0, 0, 0, 0.3),
    0 0 10px rgba(139, 0, 0, 0.3);
  text-transform: uppercase;

  @media (max-width: 480px) {
    font-size: 1.4rem;
    letter-spacing: 1.5px;
  }
`;

const LoadingText = styled(motion.div)`
  color: #fcba14;
  font-size: 1.2rem;
  font-style: italic;
`;

const CategoryCount = styled.span`
  color: #fff;
  font-size: 0.95rem;
  font-weight: 600;
  background: rgba(0, 0, 0, 0.4);
  padding: 0.6rem 1.2rem;
  border-radius: 25px;
  box-shadow:
    0 4px 8px rgba(0, 0, 0, 0.3),
    inset 0 1px 0 rgba(255, 255, 255, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(5px);

  @media (max-width: 480px) {
    font-size: 0.85rem;
    padding: 0.5rem 1rem;
  }
`;

const ImageContainer = styled.div`
  position: relative;
  height: 75%;
  overflow: hidden;
`;
const NavigationArrow = styled.button`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  ${(props) => (props.position === "left" ? "left: -25px;" : "right: -25px;")}
  width: 40px;
  height: 40px;
  background: rgba(252, 186, 20, 0.9);
  border-radius: 50%;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 20;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  opacity: 1 !important; // Force visibility
  visibility: visible !important; // Ensure arrows are always visible

  &:before {
    display: none; // Remove default slick arrow styling
  }

  svg {
    width: 20px;
    height: 20px;
    color: white;
  }

  &:hover {
    background: rgba(252, 186, 20, 1);
    transform: translateY(-50%) scale(1.1);
  }

  @media (max-width: 768px) {
    width: 35px;
    height: 35px;
    ${(props) => (props.position === "left" ? "left: -15px;" : "right: -15px;")}
  }

  @media (max-width: 480px) {
    width: 30px;
    height: 30px;
    ${(props) => (props.position === "left" ? "left: -10px;" : "right: -10px;")}
  }
`;

// Healthcare Section Styled Components
const HealthcareWrapper = styled.div`
  margin: 0 0 3rem 0;
  background: #fdf2f6;
  border-top: 1px solid rgba(233, 30, 99, 0.1);
  border-bottom: 1px solid rgba(233, 30, 99, 0.1);

  @media (max-width: 480px) {
    margin: 0 0 1rem 0;
  }
`;

const HealthcareContentContainer = styled.div`
  position: relative;
  max-width: 1200px;
  margin: 0 auto;
  padding: 3rem 24px;
  z-index: 1;

  @media (max-width: 480px) {
    padding: 2rem 16px;
  }
`;

const HealthcareTwoCol = styled.div`
  display: grid;
  grid-template-columns: 340px 1fr;
  gap: 3rem;
  align-items: start;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
`;

const HealthcareLeftPanel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
`;

const HealthcareRightPanel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  min-width: 0;
`;

/* ── Static promotional hero banner ───────────────────────────────────── */
const HealthcareStaticHero = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: 16 / 9;
  max-height: 360px;
  border-radius: 22px;
  overflow: hidden;
  background: #fdf5f7;
  box-shadow:
    0 14px 36px rgba(233, 30, 99, 0.18),
    0 3px 8px rgba(233, 30, 99, 0.08);
  border: 1px solid rgba(233, 30, 99, 0.08);

  @media (max-width: 720px) {
    aspect-ratio: 3 / 2;
    max-height: 280px;
  }
`;

const HealthcareStaticImage = styled.img`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: contain;
  object-position: center;
  transition: transform 0.5s ease;

  ${HealthcareStaticHero}:hover & {
    transform: scale(1.03);
  }
`;

const HealthcareStaticBadge = styled.span`
  position: absolute;
  top: 16px;
  left: 16px;
  z-index: 2;
  display: inline-flex;
  align-items: center;
  font-size: 0.75rem;
  font-weight: 700;
  color: #c2185b;
  background: #fff;
  padding: 7px 14px;
  border-radius: 999px;
  border: 1px solid rgba(233, 30, 99, 0.2);
  box-shadow: 0 2px 8px rgba(233, 30, 99, 0.12);
`;

/* ── Providers row header ─────────────────────────────────────────────── */
const HealthcareProvidersHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 4px;
`;

const HealthcareProvidersTitle = styled.h3`
  margin: 0;
  font-size: 1.15rem;
  font-weight: 700;
  color: #1a1a2e;
`;

const HealthcareViewAllLink = styled.button`
  display: inline-flex;
  align-items: center;
  background: transparent;
  border: none;
  color: #c2185b;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 8px;
  transition: background 0.2s;

  &:hover {
    background: rgba(233, 30, 99, 0.08);
  }
`;

const HealthcareScrollWrap = styled.div`
  position: relative;
`;

const HealthcareEyebrow = styled.span`
  display: inline-flex;
  align-items: center;
  font-size: 0.85rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #c2185b;
  background: rgba(233, 30, 99, 0.08);
  padding: 6px 14px;
  border-radius: 999px;
  width: fit-content;
`;

const HealthcareTitle = styled.h2`
  color: #1a1a2e;
  font-size: 2.4rem;
  font-weight: 800;
  margin: 0;
  line-height: 1.2;
  letter-spacing: -0.5px;

  @media (max-width: 480px) {
    font-size: 1.8rem;
  }
`;

const HealthcareSubtitle = styled.p`
  color: #555;
  font-size: 1rem;
  margin: 0;
  line-height: 1.6;
`;

const HealthcareSearchRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  background: #fff;
  border: 1.5px solid rgba(233, 30, 99, 0.25);
  border-radius: 12px;
  padding: 10px 14px;
  box-shadow: 0 2px 12px rgba(233, 30, 99, 0.08);
  transition: border-color 0.2s;

  &:focus-within {
    border-color: #e91e63;
    box-shadow: 0 2px 16px rgba(233, 30, 99, 0.18);
  }
`;

const HealthcareSearchInput = styled.input`
  flex: 1;
  border: none;
  outline: none;
  font-size: 0.92rem;
  background: transparent;
  color: #1a1a2e;

  &::placeholder {
    color: #bbb;
  }
`;

const HealthcareSearchClearButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border: none;
  background: rgba(0, 0, 0, 0.08);
  color: #777;
  border-radius: 50%;
  cursor: pointer;
  transition: background 0.2s;
  flex-shrink: 0;

  &:hover {
    background: rgba(0, 0, 0, 0.16);
  }
`;

const HealthcareNoResults = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 32px 20px;
  text-align: center;
  background: rgba(255, 255, 255, 0.7);
  border-radius: 16px;
  margin-bottom: 16px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
`;

const HealthcareNoResultsIcon = styled.span`
  font-size: 2.4rem;
  margin-bottom: 10px;
  opacity: 0.8;
`;

const HealthcareNoResultsText = styled.p`
  color: #c2185b;
  font-size: 1.05rem;
  font-weight: 600;
  margin: 0 0 6px 0;
`;

const HealthcareNoResultsSubtext = styled.p`
  color: #888;
  font-size: 0.88rem;
  margin: 0;
`;

const HealthcareProductGrid = styled.div`
  display: flex;
  overflow-x: auto;
  scroll-snap-type: x mandatory;
  gap: 16px;
  padding: 8px 4px 16px;
  scroll-behavior: smooth;
  -webkit-overflow-scrolling: touch;

  &::-webkit-scrollbar {
    height: 5px;
  }
  &::-webkit-scrollbar-track {
    background: rgba(233, 30, 99, 0.06);
    border-radius: 10px;
  }
  &::-webkit-scrollbar-thumb {
    background: #f48fb1;
    border-radius: 10px;
  }
`;

const HealthcareCard = styled.div`
  flex: 0 0 auto;
  width: 240px;
  background: #fff;
  border-radius: 16px;
  position: relative;
  scroll-snap-align: start;
  box-shadow: ${(props) =>
    props.$isHighlighted
      ? "0 8px 24px rgba(233, 30, 99, 0.3), 0 0 0 2px #e91e63"
      : "0 2px 12px rgba(0,0,0,0.08)"};
  border: 1.5px solid
    ${(props) => (props.$isHighlighted ? "#e91e63" : "rgba(233,30,99,0.1)")};
  transition:
    box-shadow 0.25s ease,
    border-color 0.25s ease;
  cursor: pointer;
  overflow: hidden;

  &:hover {
    box-shadow: 0 8px 28px rgba(233, 30, 99, 0.22);
    border-color: #e91e63;
  }
`;

const HealthcareLogoWrapper = styled.div`
  width: 100%;
  height: 160px;
  overflow: hidden;
  position: relative;
  background: ${(props) =>
    props.$isLogoFallback
      ? "linear-gradient(135deg, #fdf2f6 0%, #fce4ec 100%)"
      : "transparent"};
`;

const HealthcareLogo = styled.img`
  width: 100%;
  height: 100%;
  object-fit: ${(props) => (props.$isLogoFallback ? "contain" : "cover")};
  padding: ${(props) => (props.$isLogoFallback ? "14px" : "0")};
  transition: transform 0.35s ease;

  ${HealthcareCard}:hover & {
    transform: scale(1.06);
  }
`;

const HealthcareCardBody = styled.div`
  padding: 14px 16px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const HealthcareVendorName = styled.h3`
  margin: 0;
  font-size: 1.05rem;
  color: #1a1a2e;
  font-weight: 700;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const HealthcareVerified = styled.span`
  font-size: 0.8rem;
  color: #2e7d32;
  font-weight: 600;
  display: flex;
  align-items: center;
`;

/* Built from HealthcareCard itself, so the 240px width, the 1.5px border,
   the 160px logo well and the body's 14px/16px padding and 8px gap all
   come from the real card rather than from numbers typed in twice. The
   three blocks below stand in for the name (1.05rem), the verified line
   (0.8rem) and the small DeliveryBadge, sized to what those actually
   occupy. Total: 160 + 106 + 3 = 269px, which is the real card. */
const HealthcareCardSkeleton = () => (
  <HealthcareCard as="div" aria-hidden="true">
    <HealthcareLogoWrapper>
      <SkeletonBlock $w="100%" $h="100%" $r="0" />
    </HealthcareLogoWrapper>
    <HealthcareCardBody>
      <SkeletonBlock $w="74%" $h="20px" />
      <SkeletonBlock $w="48%" $h="16px" />
      <SkeletonBlock $w="108px" $h="27px" $r="999px" />
    </HealthcareCardBody>
  </HealthcareCard>
);

const HealthcareArrowButton = styled.button`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 40px;
  height: 40px;
  background: #e91e63;
  color: white;
  border: 2px solid white;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 10;
  transition:
    background 0.2s ease,
    transform 0.2s ease;
  box-shadow: 0 4px 12px rgba(233, 30, 99, 0.35);

  &:hover {
    background: #c2185b;
    transform: translateY(-50%) scale(1.08);
  }

  @media (max-width: 480px) {
    width: 34px;
    height: 34px;
  }
`;

const HealthcareLeftArrow = styled(HealthcareArrowButton)`
  left: -18px;
`;

const HealthcareRightArrow = styled(HealthcareArrowButton)`
  right: -18px;
`;

// Top Vendors Section Styled Components
const TopVendorsWrapper = styled.div`
  margin: 0;
  background: #fafafa;
  padding: 3.5rem 0 0.5rem;
  position: relative;
  border-top: 1px solid #f0f0f0;

  @media (max-width: 480px) {
    padding: 2rem 0 0;
  }
`;

const TopVendorsContentContainer = styled.div`
  position: relative;
  max-width: 1280px;
  margin: 0 auto;
  padding: 0 24px;
  overflow: visible;
  z-index: 1;

  @media (max-width: 480px) {
    padding: 0 12px;
  }
`;

const TopVendorsSectionHeader = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  margin-bottom: 2rem;
  gap: 1rem;
  flex-wrap: wrap;

  @media (max-width: 600px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.75rem;
  }
`;

const TopVendorsSectionLeft = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const TopVendorsSectionRight = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const TopVendorsEyebrow = styled.span`
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 2px;
  color: #ff9800;
  text-transform: uppercase;
`;

const TopVendorsViewAllLink = styled.button`
  background: none;
  border: none;
  color: #e65100;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  padding: 0;
  white-space: nowrap;
  transition: color 0.2s ease;

  &:hover {
    color: #ff9800;
  }
`;

// Vendor Search Styled Components
const VendorSearchContainer = styled.div`
  display: flex;
  align-items: center;
  background: ${(props) =>
    props.$isOpen ? "white" : "rgba(255, 255, 255, 0.9)"};
  border-radius: 25px;
  padding: 4px;
  box-shadow: ${(props) =>
    props.$isOpen
      ? "0 4px 20px rgba(230, 81, 0, 0.25)"
      : "0 2px 8px rgba(230, 81, 0, 0.15)"};
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  border: 2px solid ${(props) => (props.$isOpen ? "#ff9800" : "transparent")};
  overflow: hidden;
  margin-left: 0.5rem;

  @media (max-width: 768px) {
    position: absolute;
    right: 10px;
    top: 50%;
    transform: translateY(-50%);
  }

  @media (max-width: 480px) {
    margin-left: 0;
  }
`;

const VendorSearchInput = styled.input`
  width: ${(props) => (props.$isOpen ? "180px" : "0")};
  padding: ${(props) => (props.$isOpen ? "8px 12px" : "8px 0")};
  border: none;
  outline: none;
  font-size: 0.9rem;
  background: transparent;
  color: #bf360c;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  opacity: ${(props) => (props.$isOpen ? 1 : 0)};

  &::placeholder {
    color: #ffb74d;
  }

  @media (max-width: 768px) {
    width: ${(props) => (props.$isOpen ? "150px" : "0")};
    font-size: 0.85rem;
  }

  @media (max-width: 480px) {
    width: ${(props) => (props.$isOpen ? "120px" : "0")};
    font-size: 0.8rem;
    padding: ${(props) => (props.$isOpen ? "6px 10px" : "6px 0")};
  }
`;

const VendorSearchButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border: none;
  background: linear-gradient(135deg, #ff9800 0%, #f57c00 100%);
  color: white;
  border-radius: 50%;
  cursor: pointer;
  transition: all 0.3s ease;
  flex-shrink: 0;

  &:hover {
    background: linear-gradient(135deg, #f57c00 0%, #e65100 100%);
    transform: scale(1.05);
  }

  &:active {
    transform: scale(0.95);
  }

  @media (max-width: 480px) {
    width: 32px;
    height: 32px;
  }
`;

const VendorSearchClearButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border: none;
  background: rgba(0, 0, 0, 0.1);
  color: #666;
  border-radius: 50%;
  cursor: pointer;
  transition: all 0.2s ease;
  margin-right: 4px;

  &:hover {
    background: rgba(0, 0, 0, 0.2);
    color: #333;
  }
`;

const VendorNoResults = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  text-align: center;
  background: rgba(255, 255, 255, 0.7);
  border-radius: 16px;
  margin: 10px 20px 20px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
`;

const VendorNoResultsIcon = styled.span`
  font-size: 3rem;
  margin-bottom: 12px;
  opacity: 0.8;
`;

const VendorNoResultsText = styled.p`
  color: #e65100;
  font-size: 1.2rem;
  font-weight: 600;
  margin: 0 0 8px 0;
`;

const VendorNoResultsSubtext = styled.p`
  color: #666;
  font-size: 0.95rem;
  margin: 0;
`;

const TopVendorsTitle = styled.h2`
  color: #1a1a1a;
  font-size: 2rem;
  font-weight: 800;
  margin: 0;
  letter-spacing: -0.5px;
  font-family: "Poppins", sans-serif;
  line-height: 1.15;

  @media (max-width: 480px) {
    font-size: 1.5rem;
  }
`;

const TopVendorsSubtitle = styled.p`
  color: #666;
  font-size: 0.95rem;
  margin: 0;
  font-weight: 400;

  @media (max-width: 480px) {
    font-size: 0.875rem;
  }
`;

const TopVendorsProductGrid = styled.div`
  display: flex;
  overflow-x: auto;
  scroll-snap-type: x mandatory;
  gap: 20px;
  /* Same arrow-gutter fix as the restaurants rail — TopVendorsArrowButton is
     the identical 35x120 rectangle at left: calc(15px - 10px), so it covered
     card content here too. (Healthcare's arrows are 40px circles at -18px,
     i.e. OUTSIDE the rail, so that one needs no gutter.) */
  padding: 8px 48px 28px;
  scroll-padding-inline: 48px;
  scroll-behavior: smooth;
  -webkit-overflow-scrolling: touch;

  &::-webkit-scrollbar {
    height: 5px;
  }

  &::-webkit-scrollbar-track {
    background: #f0f0f0;
    border-radius: 10px;
  }

  &::-webkit-scrollbar-thumb {
    background: #ffb74d;
    border-radius: 10px;
  }

  @media (max-width: 480px) {
    gap: 14px;
    padding: 6px 42px 20px;
    scroll-padding-inline: 42px;
  }
`;

const TopVendorCard = styled.div`
  flex: 0 0 auto;
  width: 300px;
  /* A column, so the body can take the rail's full height and the count and
     Shop Now row sit at the bottom of every card, whether or not the vendor
     has written a tagline. */
  display: flex;
  flex-direction: column;
  background: #ffffff;
  border-radius: 16px;
  position: relative;
  scroll-snap-align: start;
  box-shadow: ${(props) =>
    props.$isHighlighted
      ? "0 0 0 2px #ff9800, 0 12px 32px rgba(255,152,0,0.2)"
      : "0 2px 12px rgba(0,0,0,0.08)"};
  transition:
    box-shadow 0.3s ease,
    transform 0.3s ease;
  cursor: pointer;
  overflow: hidden;
  border: 1px solid ${(props) => (props.$isHighlighted ? "#ff9800" : "#f0f0f0")};

  @media (max-width: 480px) {
    width: 260px;
  }
`;

const TopVendorHero = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: 600 / 440;
  overflow: hidden;
  background: #f5f5f5;
`;

const TopVendorHeroShimmer = styled.div`
  position: absolute;
  inset: 0;
  z-index: 1;
  background: linear-gradient(90deg, #e8e8e8 25%, #f4f4f4 50%, #e8e8e8 75%);
  background-size: 200% 100%;
  animation: vendorHeroShimmer 1.4s infinite;
  @keyframes vendorHeroShimmer {
    0% {
      background-position: -200% 0;
    }
    100% {
      background-position: 200% 0;
    }
  }
`;

const TopVendorHeroImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
  display: block;
  opacity: ${(p) => (p.$loaded ? 1 : 0)};
  transition:
    opacity 0.4s ease,
    transform 0.5s ease;

  ${TopVendorCard}:hover & {
    transform: scale(1.06);
  }
`;

const TopVendorHeroOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(
    to bottom,
    rgba(0, 0, 0, 0) 40%,
    rgba(0, 0, 0, 0.38) 100%
  );
`;

const TopVendorCategoryPill = styled.span`
  position: absolute;
  top: 10px;
  left: 10px;
  background: rgba(255, 255, 255, 0.92);
  color: #e65100;
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  padding: 3px 9px;
  border-radius: 20px;
  backdrop-filter: blur(4px);
`;

/* Orange kept, deliberately: this rail's accent is orange where the
   restaurant rail's is green, and the chip should belong to its section.
   Only the CLAIM changed, from "Featured" to something the data supports. */
const TopVendorVerifiedTag = styled.span`
  position: absolute;
  top: ${(props) => (props.$belowDeal ? "64px" : "10px")};
  right: 10px;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: linear-gradient(135deg, #ffa726 0%, #f7941d 45%, #e07c07 100%);
  border: 1px solid rgba(255, 255, 255, 0.55);
  color: #fff;
  font-size: 0.66rem;
  font-weight: 800;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  line-height: 1.4;
  padding: 3px 10px 3px 7px;
  border-radius: 999px;
  box-shadow:
    0 2px 8px rgba(224, 124, 7, 0.35),
    inset 0 1px 0 rgba(255, 255, 255, 0.35);

  svg {
    font-size: 13px;
  }
`;

/* Stands in for the avatar row's height when there is no distinct card
   image, so a rail mixing both kinds of vendor still lines up. Mirrors
   TopVendorAvatarRow's own box: -24 top, 52 tall, 10 bottom = 38. */
const TopVendorBodySpacer = styled.div`
  height: 14px;
`;

const TopVendorCountPill = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 4px 9px;
  border-radius: 999px;
  background: #fff4e6;
  color: #b45309;
  font-size: 0.72rem;
  font-weight: 700;
  white-space: nowrap;
`;

const TopVendorBody = styled.div`
  flex: 1;
  padding: 0 16px 18px;
  display: flex;
  flex-direction: column;
`;

const TopVendorAvatarRow = styled.div`
  display: flex;
  align-items: flex-end;
  margin-top: -24px;
  margin-bottom: 10px;
  position: relative;
  z-index: 2;
`;

const TopVendorAvatar = styled.img`
  width: 52px;
  height: 52px;
  border-radius: 12px;
  object-fit: cover;
  border: 3px solid #ffffff;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  background: #f5f5f5;
`;

const TopVendorVerifiedBadge = styled.div`
  width: 20px;
  height: 20px;
  background: #ff9800;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-left: -8px;
  margin-bottom: 0;
  border: 2px solid white;
  flex-shrink: 0;
`;

const TopVendorName = styled.h3`
  margin: 0 0 4px;
  font-size: 1.05rem;
  color: #1a1a1a;
  font-weight: 700;
  letter-spacing: -0.2px;
  font-family: "Poppins", sans-serif;
  line-height: 1.3;
`;

/* min-height reserves BOTH lines the clamp allows, whether or not this
   vendor has a tagline and whether or not it wraps. Without it the rail
   was uneven even after loading: a vendor with no tagline sat ~47px
   shorter than the card beside it, and no skeleton could match a card
   whose height depended on copy nobody had fetched yet. 2 lines x 1.4
   line-height = 2.8em. */
/* The vendor's own tagline under its name on the landing page's restaurant,
   shop and pharmacy cards. Nothing renders when a vendor has not written one. */
const CardTagline = styled(VendorTagline)`
  margin: 0 0 8px;
  width: 100%;
`;

const TopVendorTagline = styled.p`
  margin: 0 0 10px;
  font-size: 0.84rem;
  /* Was #888, which is the colour of a caption nobody wrote. This line now
     carries what the vendor sells, so it earns a readable weight and a hue
     from the page's own family rather than sitting there as grey filler. */
  color: #5b6b3a;
  font-weight: 500;
  line-height: 1.4;
  min-height: 2.35em;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const TopVendorMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  margin-top: auto;
  margin-bottom: 12px;
`;

const TopVendorCTA = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: 10px 0;
  background: #1a1a1a;
  color: white;
  border: none;
  border-radius: 10px;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.25s ease;
  letter-spacing: 0.2px;

  &:hover {
    background: #ff9800;
    transform: translateY(-1px);
    box-shadow: 0 4px 14px rgba(255, 152, 0, 0.35);
  }

  &:active {
    transform: translateY(0);
  }
`;

/* Built from TopVendorCard, so the 300px width (260 at <=480), the border
   and the hero's `aspect-ratio: 600/440` all come from the real card —
   which matters here more than anywhere else on the page, because that
   hero is aspect-driven and therefore changes height with the card width.
   A hand-sized skeleton could only ever be right at one breakpoint.
   The body blocks mirror the avatar row, name, the two reserved tagline
   lines, the DeliveryBadge and the CTA. */
const TopVendorCardSkeleton = () => (
  <TopVendorCard as="div" aria-hidden="true">
    <TopVendorHero>
      <SkeletonBlock $w="100%" $h="100%" $r="0" />
    </TopVendorHero>
    <TopVendorBody>
      <TopVendorAvatarRow>
        <SkeletonBlock $w="52px" $h="52px" $r="12px" />
      </TopVendorAvatarRow>
      <SkeletonBlock $w="68%" $h="22px" $mb="4px" />
      <SkeletonBlock $w="100%" $h="17px" $mb="4px" />
      <SkeletonBlock $w="54%" $h="17px" $mb="10px" />
      <SkeletonBlock $w="96px" $h="27px" $r="999px" $mb="12px" />
      <SkeletonBlock $w="100%" $h="37px" $r="10px" />
    </TopVendorBody>
  </TopVendorCard>
);

const TopVendorsArrowButton = styled.button`
  position: absolute;
  top: 57%;
  transform: translateY(-50%);
  width: 35px;
  height: 120px;
  background: linear-gradient(135deg, #ff9800 0%, #ffb74d 100%);
  color: white;
  border: 1px solid white;
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 10;
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px rgba(255, 152, 0, 0.4);

  &:hover {
    background: linear-gradient(135deg, #f57c00 0%, #ff9800 100%);
    transform: translateY(-50%) scaleX(1.1);
    box-shadow: 0 6px 16px rgba(255, 152, 0, 0.5);
  }

  &:active {
    transform: translateY(-50%) scaleX(0.95);
  }

  @media (max-width: 480px) {
    width: 30px;
    height: 100px;
  }
`;

const TopVendorsLeftArrow = styled(TopVendorsArrowButton)`
  left: calc(15px - 10px);
`;

const TopVendorsRightArrow = styled(TopVendorsArrowButton)`
  right: calc(15px - 10px);
`;

const MoreVendorsButtonWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 2.5rem;
  padding-bottom: 1rem;
`;

const MoreVendorsButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 14px 32px;
  background: linear-gradient(135deg, #ff9800 0%, #f57c00 100%);
  color: white;
  border: none;
  border-radius: 12px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px rgba(255, 152, 0, 0.3);
  font-family: "Poppins", sans-serif;

  &:hover {
    background: linear-gradient(135deg, #f57c00 0%, #e65100 100%);
    transform: translateY(-2px);
    box-shadow: 0 6px 16px rgba(255, 152, 0, 0.4);
  }

  &:active {
    transform: translateY(0);
  }

  @media (max-width: 768px) {
    padding: 12px 28px;
    font-size: 0.95rem;
  }

  @media (max-width: 480px) {
    padding: 10px 24px;
    font-size: 0.9rem;
  }
`;

// Top Restaurants Section Styled Components
const TopRestaurantsWrapper = styled.div`
  margin: 0;
  background: linear-gradient(135deg, #e8f5e9 0%, #dff555 30%, #ddf639ff 100%);
  padding: 1rem 0 2.5rem 0;
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    top: -50%;
    left: -50%;
    width: 200%;
    height: 200%;
    background: radial-gradient(
      circle,
      rgba(76, 175, 80, 0.1) 0%,
      transparent 70%
    );
    animation: pulse 8s ease-in-out infinite;
  }

  @keyframes pulse {
    0%,
    100% {
      transform: scale(1);
      opacity: 0.5;
    }
    50% {
      transform: scale(1.1);
      opacity: 0.8;
    }
  }

  @media (max-width: 480px) {
    margin: 0;
    padding: 1rem 0 0 0;
  }
`;

const TopRestaurantsContentContainer = styled.div`
  position: relative;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
  overflow: visible;
  z-index: 1;

  @media (max-width: 480px) {
    padding: 0 5px;
    margin: 15px;
  }
`;

const TopRestaurantsHeader = styled.div`
  text-align: center;
  margin-bottom: 2rem;
  position: relative;
  padding: 0 10px;
`;

const TopRestaurantsHeaderContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
`;

const TopRestaurantsTitleWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  justify-content: center;
  flex-wrap: wrap;
  position: relative;
  width: 100%;

  @media (max-width: 768px) {
    padding-right: 50px;
  }
`;

const TopRestaurantsIcon = styled.span`
  font-size: 2.5rem;
  animation: bounce 2s ease-in-out infinite;

  @keyframes bounce {
    0%,
    100% {
      transform: translateY(0);
    }
    50% {
      transform: translateY(-10px);
    }
  }

  @media (max-width: 480px) {
    font-size: 2rem;
  }
`;

const TopRestaurantsIconImg = styled.img`
  height: 3rem;
  width: auto;
  display: block;
  object-fit: contain;

  @media (max-width: 480px) {
    height: 2.2rem;
  }
`;

const TopRestaurantsTitle = styled.h2`
  color: #2e7d32;
  font-size: 2.2rem;
  font-weight: 700;
  margin: 0;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.1);
  letter-spacing: 0.5px;

  @media (max-width: 480px) {
    font-size: 1.6rem;
  }
`;

const TopRestaurantsSubtitle = styled.p`
  color: #1b5e20;
  font-size: 1.1rem;
  margin: 0;
  font-weight: 500;
  opacity: 0.9;

  @media (max-width: 480px) {
    font-size: 0.9rem;
  }
`;

// Restaurant Search Styled Component
const RestaurantSearchContainer = styled.div`
  display: flex;
  align-items: center;
  background: ${(props) =>
    props.$isOpen ? "white" : "rgba(255, 255, 255, 0.9)"};
  border-radius: 25px;
  padding: 4px;
  box-shadow: ${(props) =>
    props.$isOpen
      ? "0 4px 20px rgba(46, 125, 50, 0.25)"
      : "0 2px 8px rgba(46, 125, 50, 0.15)"};
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  border: 2px solid ${(props) => (props.$isOpen ? "#4caf50" : "transparent")};
  overflow: hidden;
  margin-left: 0.5rem;

  @media (max-width: 768px) {
    position: absolute;
    right: 10px;
    top: 50%;
    transform: translateY(-50%);
  }

  @media (max-width: 480px) {
    margin-left: 0;
  }
`;

const RestaurantSearchInput = styled.input`
  width: ${(props) => (props.$isOpen ? "180px" : "0")};
  padding: ${(props) => (props.$isOpen ? "8px 12px" : "8px 0")};
  border: none;
  outline: none;
  font-size: 0.9rem;
  background: transparent;
  color: #1b5e20;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  opacity: ${(props) => (props.$isOpen ? 1 : 0)};

  &::placeholder {
    color: #81c784;
  }

  @media (max-width: 768px) {
    width: ${(props) => (props.$isOpen ? "150px" : "0")};
    font-size: 0.85rem;
  }

  @media (max-width: 480px) {
    width: ${(props) => (props.$isOpen ? "120px" : "0")};
    font-size: 0.8rem;
    padding: ${(props) => (props.$isOpen ? "6px 10px" : "6px 0")};
  }
`;

const RestaurantSearchButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border: none;
  background: linear-gradient(135deg, #4caf50 0%, #388e3c 100%);
  color: white;
  border-radius: 50%;
  cursor: pointer;
  transition: all 0.3s ease;
  flex-shrink: 0;

  &:hover {
    background: linear-gradient(135deg, #388e3c 0%, #2e7d32 100%);
    transform: scale(1.05);
  }

  &:active {
    transform: scale(0.95);
  }

  @media (max-width: 480px) {
    width: 32px;
    height: 32px;
  }
`;

const RestaurantSearchClearButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border: none;
  background: rgba(0, 0, 0, 0.1);
  color: #666;
  border-radius: 50%;
  cursor: pointer;
  transition: all 0.2s ease;
  margin-right: 4px;

  &:hover {
    background: rgba(0, 0, 0, 0.2);
    color: #333;
  }
`;

const RestaurantNoResults = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  text-align: center;
  background: rgba(255, 255, 255, 0.7);
  border-radius: 16px;
  margin: 10px 20px 20px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
`;

const RestaurantNoResultsIcon = styled.span`
  font-size: 3rem;
  margin-bottom: 12px;
  opacity: 0.8;
`;

const RestaurantNoResultsText = styled.p`
  color: #2e7d32;
  font-size: 1.2rem;
  font-weight: 600;
  margin: 0 0 8px 0;
`;

const RestaurantNoResultsSubtext = styled.p`
  color: #666;
  font-size: 0.95rem;
  margin: 0;
`;

const TopRestaurantsProductGrid = styled.div`
  display: flex;
  overflow-x: auto;
  scroll-snap-type: x mandatory;
  gap: 20px;
  /* Reserve the arrow gutters. The left/right arrows are 35px wide, sit 5px
     inside this rail's own container and paint at z-index 10 — with the old
     10px padding they landed directly on the first card's name and address.
     48px = 5 (offset) + 35 (arrow) + 8 (breathing room).
     scroll-padding-inline makes scroll-snap respect the same gutter, so a
     snapped card stops clear of the arrow instead of under it. */
  padding: 0 48px 20px 48px;
  scroll-padding-inline: 48px;
  margin-top: -14px;
  scroll-behavior: smooth;
  -webkit-overflow-scrolling: touch;

  &::-webkit-scrollbar {
    height: 8px;
  }

  &::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.3);
    border-radius: 10px;
  }

  &::-webkit-scrollbar-thumb {
    background: #4caf50;
    border-radius: 10px;

    &:hover {
      background: #388e3c;
    }
  }

  @media (max-width: 480px) {
    gap: 15px;
    /* Arrow shrinks to 30px here, so the gutter can too. */
    padding: 0 42px 15px 42px;
    scroll-padding-inline: 42px;
    margin-top: -8px;
  }
`;

const TopRestaurantCard = styled.div`
  flex: 0 0 auto;
  width: 320px;
  margin: 10px;
  background: ${(props) => (props.$isHighlighted ? "#f4fae8" : "#ffffff")};
  padding: 0;
  border-radius: 16px;
  position: relative;
  scroll-snap-align: start;
  box-shadow: ${(props) =>
    props.$isHighlighted
      ? "0 4px 12px rgba(131, 181, 36, 0.25)"
      : "0 2px 8px rgba(0, 0, 0, 0.08)"};
  transition: all 0.3s ease;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  cursor: pointer;
  overflow: hidden;
  border: 1px solid
    ${(props) => (props.$isHighlighted ? "#83b524" : "rgba(0, 0, 0, 0.06)")};

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.12);
    border-color: #83b524;
  }

  @media (max-width: 480px) {
    width: 280px;
  }
`;

const TopRestaurantLogoWrapper = styled.div`
  width: 100%;
  height: 150px;
  background: #ffffff;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  box-shadow: 0 6px 14px -6px rgba(16, 42, 67, 0.14);
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 14px 18px;
  padding: 10px 14px;
`;

const TopRestaurantLogo = styled.img`
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
`;

/* Was a gold "PREMIUM" chip. Two problems with that, and the second is the
   real one:

   1. `promoted` is a bare admin boolean on the vendor model — no billing,
      no expiry, no criteria. It does not encode a quality or price tier,
      so "Premium" claimed something the data cannot support, and it primes
      PRICE at the exact moment we want the customer thinking about food.
      ("Promoted" would be no better: it reads as paid placement, which
      this also is not.)
   2. `activeRestaurants` already filters this rail to
      `sellerVerification === "VERIFIED"`. The platform is doing a real
      check on every kitchen here and never telling the customer, while
      shouting a word that means nothing.

   Verified is the one claim that is both true and useful to a first-time
   customer, whose actual question is "is this a real business and will my
   order arrive". Platform green #22c55e, matching the verified chip on
   BrandShopPage — deliberately NOT a brand accent, so the mark reads as
   Lusuku vouching rather than the vendor decorating itself. */
const TopRestaurantVerifiedTag = styled.span`
  position: absolute;
  top: 10px;
  right: 10px;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 10.5px;
  font-weight: 800;
  letter-spacing: 0.6px;
  text-transform: uppercase;
  line-height: 1.4;
  color: #fff;
  background: linear-gradient(135deg, #34d072 0%, #22c55e 45%, #16a34a 100%);
  border: 1px solid rgba(255, 255, 255, 0.55);
  padding: 3px 11px 3px 8px;
  border-radius: 999px;
  box-shadow:
    0 2px 8px rgba(22, 163, 74, 0.35),
    inset 0 1px 0 rgba(255, 255, 255, 0.35);
  text-shadow: 0 1px 1px rgba(6, 78, 40, 0.35);

  svg {
    font-size: 13px;
  }
`;

const TopRestaurantInfo = styled.div`
  /* Fills the card, so the address and Order row sit at the bottom of every
     card in the rail, with or without a tagline above them. */
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: 100%;
  min-width: 0;
  padding: 12px 16px 14px;
`;

const TopRestaurantName = styled.h3`
  margin: 0 0 4px;
  font-size: 16px;
  color: #212121;
  font-weight: 700;
  text-align: left;
  width: 100%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const TopRestaurantAddress = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  width: 100%;
  min-width: 0;
  color: #757575;
  font-size: 13px;
  margin: auto 0 10px;

  svg {
    font-size: 15px;
    color: #9e9e9e;
    flex-shrink: 0;
  }

  span {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    min-width: 0;
  }
`;

const TopRestaurantOrderCta = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 2px;
  font-size: 14px;
  font-weight: 700;
  color: #83b524;

  svg {
    font-size: 18px;
    transition: transform 0.2s ease;
  }

  ${TopRestaurantCard}:hover & svg {
    transform: translateX(3px);
  }
`;

/* Built from TopRestaurantCard, so the 320px width (280 at <=480), the
   10px margin and the 150px logo well are the real card's, not a second
   copy of them. The old shared skeleton was a flat 300px wide with a
   20px margin-RIGHT only: 20px of horizontal drift per card at desktop,
   20px the other way below 480px, and 65-85px too tall, so the rail
   visibly collapsed the moment the restaurants arrived.
   The three blocks mirror TopRestaurantInfo's rows: name 19px + 4
   margin, address 16px + 10 margin, CTA 18px — 67px inside 26px of
   padding, which is the 93px the real info block occupies. */
const TopRestaurantCardSkeleton = () => (
  <TopRestaurantCard as="div" aria-hidden="true">
    <TopRestaurantLogoWrapper>
      <SkeletonBlock $w="62%" $h="72px" $r="8px" />
    </TopRestaurantLogoWrapper>
    <TopRestaurantInfo>
      <SkeletonBlock $w="70%" $h="19px" $mb="4px" />
      <SkeletonBlock $w="88%" $h="16px" $mb="10px" />
      <SkeletonBlock $w="42%" $h="18px" />
    </TopRestaurantInfo>
  </TopRestaurantCard>
);

const TopRestaurantsArrowButton = styled.button`
  position: absolute;
  top: 60%;
  transform: translateY(-50%);
  width: 35px;
  height: 120px;
  background: linear-gradient(135deg, #22b026ff 0%, #106313ff 100%);
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 10;
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px rgba(76, 175, 80, 0.4);

  &:hover {
    background: linear-gradient(135deg, #388e3c 0%, #4caf50 100%);
    transform: translateY(-50%) scaleX(1.1);
    box-shadow: 0 6px 16px rgba(76, 175, 80, 0.5);
  }

  &:active {
    transform: translateY(-50%) scaleX(0.95);
  }

  @media (max-width: 480px) {
    width: 30px;
    height: 100px;
  }
`;

const TopRestaurantsLeftArrow = styled(TopRestaurantsArrowButton)`
  left: calc(15px - 10px);
`;

const TopRestaurantsRightArrow = styled(TopRestaurantsArrowButton)`
  right: calc(15px - 10px);
`;

const MoreRestaurantsButtonWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 2.5rem;
  padding-bottom: 1rem;
`;

const MoreRestaurantsButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 14px 32px;
  background: linear-gradient(135deg, #0c7b10ff 0%, #106313ff 100%);
  color: white;
  border: none;
  border-radius: 12px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px rgba(34, 176, 38, 0.3);
  font-family: "Poppins", sans-serif;

  &:hover {
    background: linear-gradient(135deg, #1a8a1f 0%, #0d4a0f 100%);
    transform: translateY(-2px);
    box-shadow: 0 6px 16px rgba(34, 176, 38, 0.4);
  }

  &:active {
    transform: translateY(0);
  }

  @media (max-width: 768px) {
    padding: 12px 28px;
    font-size: 0.95rem;
  }

  @media (max-width: 480px) {
    padding: 10px 24px;
    font-size: 0.9rem;
  }
`;

const MoreHealthcareButton = styled.button`
  display: inline-flex;
  align-items: center;
  padding: 12px 24px;
  background: linear-gradient(135deg, #c2185b 0%, #e91e63 100%);
  color: white;
  border: none;
  border-radius: 12px;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  transition:
    transform 0.2s ease,
    box-shadow 0.2s ease;
  box-shadow: 0 4px 14px rgba(233, 30, 99, 0.35);
  font-family: "Poppins", sans-serif;
  align-self: flex-start;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(233, 30, 99, 0.45);
  }

  &:active {
    transform: translateY(0);
  }
`;

// ── AI Shopping Entry Points ──────────────────────────────────────────────────

const AIFloatingButton = styled.a`
  position: fixed;
  bottom: 28px;
  right: 24px;
  z-index: 9999;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 20px;
  background: linear-gradient(135deg, #10a37f 0%, #0d8c6d 100%);
  color: #fff;
  border-radius: 50px;
  font-family: "Poppins", sans-serif;
  font-size: 0.9rem;
  font-weight: 600;
  text-decoration: none;
  box-shadow: 0 4px 20px rgba(16, 163, 127, 0.45);
  transition: all 0.25s ease;
  white-space: nowrap;

  &:hover {
    background: linear-gradient(135deg, #0d8c6d 0%, #0a7560 100%);
    transform: translateY(-3px);
    box-shadow: 0 8px 28px rgba(16, 163, 127, 0.55);
    color: #fff;
    text-decoration: none;
  }

  &:active {
    transform: translateY(-1px);
  }

  @media (max-width: 480px) {
    padding: 10px 16px;
    font-size: 0.82rem;
    bottom: 20px;
    right: 16px;
  }
`;

const AIFloatingIcon = styled.span`
  font-size: 1.1rem;
  line-height: 1;
`;

const AIFloatingLabel = styled.span`
  @media (max-width: 360px) {
    display: none;
  }
`;
